"use client";

import type {
  AuthenticatedUser,
  AuthErrorResponse,
  CompetitionDashboardResponse,
  CompetitionEntryResponse,
  CompetitionPickView,
  CompetitionPoolDetail,
  CompetitionPoolSummary,
  DemoAccountView,
  DemoHistoryResponse,
  DemoTradeView,
  GameMode,
  GameModesResponse,
  LiveHistoryResponse,
  LiveTradeResponse,
  LiveTradeView,
  UnifiedHistoryItem,
  PredictionView,
  PriceSeriesResponse,
  PriceStreamMessage,
  ReferralMeResponse,
  RoundStateResponse,
  RoundView,
  SolanaDepositHistoryResponse,
  SolanaWithdrawalHistoryResponse,
  SolanaWithdrawalResponse,
  SolanaWalletResponse,
} from "@priceon/shared";
import { PREDICTION_LEAD_MS, quoteDemoTrade, quoteLiveTrade } from "@priceon/shared";
import {
  Activity,
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  ArrowUpRight,
  Copy,
  Clock3,
  Flame,
  Gamepad2,
  History,
  LogIn,
  LogOut,
  Medal,
  Share2,
  Send,
  Swords,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Target,
  Trophy,
  Users,
  UserRound,
  Wallet,
  WifiOff,
  X,
  Download,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  buildBattleTimeline,
  getBattleProgressPercent,
  type BattleRoundDisplay,
} from "./battle";
import {
  formatDemoDetailSol,
  formatLiveDetailSol,
  formatPrimarySol,
  formatSignedPrimarySol,
} from "./amount-format";
import {
  appendLivePricePoint,
  canPaintPriceChart,
  formatPriceDelta,
  generateCandlesticks,
  getChartTone,
  canReuseChartPriceDomain,
  getDynamicStakePresets,
  getExpandedPriceDomain,
  getLiveTradeStatus,
  getNextRoundRefreshDelay,
  getResultOverlayStorageKey,
  getRoundPhase,
  areBattlesEnabled,
  getRoundProgressPercent,
  getRoundTimePercent,
  getSecondsRemaining,
  getServerOffset,
  holdPaintedPriceChart,
  isDecisionPhase,
  isPredictionWindowOpen,
  isPriceAvailable,
  lockMiniAppBootViewportHeight,
  MINI_APP_PLAY_BOOT_TIMEOUT_MS,
  MINI_APP_VIEWPORT_STABLE_TIMEOUT_MS,
  resolveGameModeAfterModesLoad,
  resolveMiniAppBootViewportHeight,
  resolvePlayChartRound,
  resolveStakeSelection,
  shouldShowResultOverlay,
  shouldRevealMiniAppPlay,
  isStakeAllowed,
  toDisplayRound,
  toScheduledDisplayRound,
  type ChartPriceDomainKey,
  type DisplayRound,
  type Direction,
  type PaintedPriceChart,
  type PriceConnectionState,
  type PricePoint,
  type PriceSeriesStatus,
} from "./game";
import {
  hasActiveWithdrawals,
  hasTerminalWithdrawalTransition,
} from "./wallet";
import {
  buildResultShareLinks,
  isShareableResultStatus,
  type ShareableResultStatus,
} from "./share";

type AuthState = "checking" | "authenticated" | "registration-required" | "error";
type Tab = "play" | "battle" | "stats" | "profile";
type ConnectionState = PriceConnectionState;
type DataStatus = "idle" | "loading" | "ready" | "error";

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe?: {
          start_param?: string;
          user?: { id?: number; allows_write_to_pm?: boolean };
        };
        isExpanded?: boolean;
        viewportHeight?: number;
        viewportStableHeight?: number;
        ready(): void;
        expand(): void;
        onEvent?(event: string, callback: (...args: unknown[]) => void): void;
        offEvent?(event: string, callback: (...args: unknown[]) => void): void;
        openTelegramLink?(url: string): void;
        openLink?(url: string, options?: { try_instant_view?: boolean }): void;
        requestWriteAccess?(callback?: (allowed: boolean) => void): void;
        setHeaderColor?(color: string): void;
        setBackgroundColor?(color: string): void;
        HapticFeedback?: {
          impactOccurred(style: "light" | "medium" | "heavy" | "rigid" | "soft"): void;
          notificationOccurred(type: "error" | "success" | "warning"): void;
          selectionChanged(): void;
        };
      };
    };
  }
}

const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || "/api";
const botUsername = process.env.NEXT_PUBLIC_TELEGRAM_BOT_USERNAME;
const communityUrl = "https://t.me/PriceOnCommunity";
const decisionPhaseExitMs = 320;
const resultStampMs = 1_800;
const resultShareMs = 8_000;
const withdrawalPollIntervalMs = 5_000;

function triggerHaptic(event: "pick-open" | "locked" | "won" | "lost" | "void"): void {
  if (typeof window === "undefined") return;
  const feedback = window.Telegram?.WebApp?.HapticFeedback;
  if (!feedback) return;
  try {
    if (event === "pick-open") feedback.selectionChanged();
    else if (event === "locked") feedback.impactOccurred("medium");
    else feedback.notificationOccurred(event === "won" ? "success" : event === "lost" ? "error" : "warning");
  } catch {
    // Haptics are enhancement-only and must not affect submitted trades.
  }
}

export default function Home() {
  const [authState, setAuthState] = useState<AuthState>("checking");
  const [authMessage, setAuthMessage] = useState("Verifying your Telegram session...");
  const [authorization, setAuthorization] = useState<string>();
  const [user, setUser] = useState<AuthenticatedUser>();
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [activeTab, setActiveTab] = useState<Tab>("play");
  const shownResultKeysRef = useRef<Record<string, string>>({});
  const [now, setNow] = useState(() => Date.now());
  const [serverOffset, setServerOffset] = useState(0);
  const [roundState, setRoundState] = useState<RoundStateResponse>();
  const [roundMessage, setRoundMessage] = useState("Loading the current round...");
  const [predictions, setPredictions] = useState<Record<string, PredictionView>>({});
  const [predictionMessage, setPredictionMessage] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState<UnifiedHistoryItem[]>([]);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [historyStatus, setHistoryStatus] = useState<DataStatus>("idle");
  const [competitionDashboard, setCompetitionDashboard] = useState<CompetitionDashboardResponse>();
  const [competitionStatus, setCompetitionStatus] = useState<DataStatus>("idle");
  const [selectedCompetitionId, setSelectedCompetitionId] = useState<string>();
  const [competitionDetail, setCompetitionDetail] = useState<CompetitionPoolDetail>();
  const [competitionAction, setCompetitionAction] = useState<string>();
  const [competitionSubmitting, setCompetitionSubmitting] = useState(false);
  const [battleAlert, setBattleAlert] = useState<CompetitionPoolSummary>();
  const [demoAccount, setDemoAccount] = useState<DemoAccountView>();
  const [demoStatus, setDemoStatus] = useState<DataStatus>("idle");
  const [wallet, setWallet] = useState<SolanaWalletResponse>();
  const [walletDeposits, setWalletDeposits] = useState<SolanaDepositHistoryResponse>();
  const [walletWithdrawals, setWalletWithdrawals] = useState<SolanaWithdrawalHistoryResponse>();
  const [walletStatus, setWalletStatus] = useState<DataStatus>("idle");
  const [walletBalanceRefreshPending, setWalletBalanceRefreshPending] = useState(false);
  const [referral, setReferral] = useState<ReferralMeResponse>();
  const [referralCode, setReferralCode] = useState(() => getReferralStartCode() ?? "");
  const [referralMessage, setReferralMessage] = useState<string>();
  const [referralSubmitting, setReferralSubmitting] = useState(false);
  const [demoTrades, setDemoTrades] = useState<Record<string, DemoTradeView>>({});
  const [gameMode, setGameMode] = useState<GameMode>("demo");
  const [gameModes, setGameModes] = useState<GameModesResponse>();
  const [liveTrades, setLiveTrades] = useState<Record<string, LiveTradeView>>({});
  const [liveHistory, setLiveHistory] = useState<UnifiedHistoryItem[]>([]);
  const [liveHistoryCursor, setLiveHistoryCursor] = useState<string | null>(null);
  const [liveHistoryStatus, setLiveHistoryStatus] = useState<DataStatus>("idle");
  const [stakeSol, setStakeSol] = useState("0.10");
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [tutorialShownThisSession, setTutorialShownThisSession] = useState(false);
  const [livePrice, setLivePrice] = useState<number>();
  const [priceSeries, setPriceSeries] = useState<PricePoint[]>([]);
  const [priceSeriesStatus, setPriceSeriesStatus] = useState<PriceSeriesStatus>("loading");
  const [viewportStable, setViewportStable] = useState(false);
  const [roundStateSettled, setRoundStateSettled] = useState(false);
  const [playBootTimedOut, setPlayBootTimedOut] = useState(false);
  const [playRevealed, setPlayRevealed] = useState(false);
  const [pendingDirection, setPendingDirection] = useState<Direction | undefined>(undefined);
  const [seriesRefreshToken, setSeriesRefreshToken] = useState(0);
  const roundStateRef = useRef<RoundStateResponse | undefined>(undefined);
  const activeRoundRef = useRef<DisplayRound | undefined>(undefined);
  const serverOffsetRef = useRef(0);
  const lastPriceReceivedAtRef = useRef(0);
  const roundRequestRef = useRef(0);
  const historyRequestRef = useRef(0);
  const lastResumeSyncAtRef = useRef(0);
  const resumePendingRef = useRef(false);
  const walletWithdrawalsRef = useRef<SolanaWithdrawalHistoryResponse | undefined>(undefined);
  const withdrawalRefreshRef = useRef<Promise<void> | undefined>(undefined);
  const walletBalanceRefreshRef = useRef<Promise<boolean> | undefined>(undefined);
  const stakeSelectionContextRef = useRef<string | undefined>(undefined);
  const priceReconnectRef = useRef<() => void>(() => undefined);
  const pricePauseRef = useRef<() => void>(() => undefined);
  const previousRoundRef = useRef<string | undefined>(undefined);
  const selectedCompetitionRef = useRef<string | undefined>(undefined);
  const battleStartHandledRef = useRef(false);
  const competitionAccountSyncRef = useRef<string | undefined>(undefined);
  const liveModeConfirmedRef = useRef(false);
  const gameModeInitializedRef = useRef(false);
  const resultFeedbackRef = useRef<string | undefined>(undefined);
  const pickWindowFeedbackRef = useRef<string | undefined>(undefined);

  const activeRound = useMemo(() => toDisplayRound(roundState?.current), [roundState?.current]);
  const previousRound = useMemo(() => toDisplayRound(roundState?.previous), [roundState?.previous]);
  const upcomingRound = roundState?.upcoming;
  const currentPrediction = roundState?.current ? predictions[roundState.current.id] : undefined;
  const previousPrediction = roundState?.previous ? predictions[roundState.previous.id] : undefined;
  const currentDemoTrade = roundState?.current ? demoTrades[roundState.current.id] : undefined;
  const upcomingDemoTrade = upcomingRound ? demoTrades[upcomingRound.id] : undefined;
  const previousDemoTrade = roundState?.previous ? demoTrades[roundState.previous.id] : undefined;
  const currentLiveTrade = roundState?.current ? liveTrades[roundState.current.id] : undefined;
  const upcomingLiveTrade = upcomingRound ? liveTrades[upcomingRound.id] : undefined;
  const previousLiveTrade = roundState?.previous ? liveTrades[roundState.previous.id] : undefined;
  const battlesEnabled = areBattlesEnabled(gameModes);
  const activeStakeDirection = pendingDirection
    ?? (gameMode === "live" ? upcomingLiveTrade ?? currentLiveTrade : upcomingDemoTrade ?? currentDemoTrade)?.direction;
  const availableStakeBalance = gameMode === "live"
    ? gameModes?.live.balance.availableSol
    : demoAccount?.availableBalance;
  const maximumStakeSol = gameMode === "live"
    ? gameModes?.live.limits.maximumStakeSol
    : undefined;
  const stakePresets = useMemo(
    () => getDynamicStakePresets(availableStakeBalance, maximumStakeSol),
    [availableStakeBalance, maximumStakeSol],
  );
  const stakeAvailability = useMemo(() => gameMode === "live" ? {
    mode: "live" as const,
    liveEnabled: gameModes?.live.enabled,
    liveAvailableLamports: gameModes?.live.balance.availableLamports,
    liveMinimumStakeLamports: gameModes?.live.limits.minimumStakeLamports,
    liveMaximumStakeLamports: gameModes?.live.limits.maximumStakeLamports,
  } : {
    mode: "demo" as const,
    demoAvailableBalance: demoAccount?.availableBalance,
  }, [demoAccount?.availableBalance, gameMode, gameModes?.live]);
  const stakeSelectionContext = gameMode === "live"
    ? [
      gameMode,
      gameModes?.live.enabled,
      gameModes?.live.balance.availableLamports,
      gameModes?.live.limits.minimumStakeLamports,
      gameModes?.live.limits.maximumStakeLamports,
    ].join(":")
    : `${gameMode}:${demoAccount?.availableBalance ?? ""}`;
  const blockingCompetition = battlesEnabled
    ? competitionDashboard?.myCompetitions.find((competition) => (
      competition.status === "active" && now >= Date.parse(competition.entryClosesAt) && now < Date.parse(competition.endsAt)
    ))
    : undefined;

  useEffect(() => {
    selectedCompetitionRef.current = selectedCompetitionId;
  }, [selectedCompetitionId]);

  useEffect(() => {
    activeRoundRef.current = activeRound;
  }, [activeRound]);

  useEffect(() => {
    walletWithdrawalsRef.current = walletWithdrawals;
  }, [walletWithdrawals]);

  useEffect(() => {
    if (availableStakeBalance === undefined || activeStakeDirection) return;
    if (stakeSelectionContextRef.current === stakeSelectionContext) return;
    stakeSelectionContextRef.current = stakeSelectionContext;
    const nextStake = resolveStakeSelection(
      stakeSol,
      stakePresets,
      (amount) => isStakeAllowed(amount, stakeAvailability),
    );
    if (nextStake !== stakeSol) setStakeSol(nextStake);
  }, [activeStakeDirection, availableStakeBalance, stakeAvailability, stakePresets, stakeSelectionContext, stakeSol]);

  useEffect(() => {
    if (!roundState) return;
    const recentTrade = gameMode === "live" ? previousLiveTrade : previousDemoTrade;
    const result = recentTrade?.status !== "pending"
      ? recentTrade?.status
      : previousPrediction?.outcome !== "pending"
        ? previousPrediction?.outcome
        : undefined;
    const resultId = recentTrade?.roundId ?? previousPrediction?.roundId;
    const key = result && resultId ? `${gameMode}:${resultId}:${result}` : "";

    if (resultFeedbackRef.current === undefined) {
      if (key) resultFeedbackRef.current = key;
      return;
    }
    if (key && key !== resultFeedbackRef.current && activeTab === "play" && document.visibilityState === "visible") {
      triggerHaptic(result === "won" ? "won" : result === "lost" ? "lost" : "void");
    }
    resultFeedbackRef.current = key;
  }, [activeTab, gameMode, previousDemoTrade, previousLiveTrade, previousPrediction, roundState]);

  useEffect(() => {
    if (!roundState) return;
    const key = activeRound && upcomingRound && isPredictionWindowOpen(now, upcomingRound)
      ? upcomingRound.id
      : "";
    if (pickWindowFeedbackRef.current === undefined) {
      pickWindowFeedbackRef.current = key;
      return;
    }
    if (key && key !== pickWindowFeedbackRef.current && activeTab === "play" && document.visibilityState === "visible") {
      triggerHaptic("pick-open");
    }
    pickWindowFeedbackRef.current = key;
  }, [activeRound, activeTab, now, roundState, upcomingRound]);

  useEffect(() => {
    const updateConnection = () => {
      if (!navigator.onLine) setConnection("device-offline");
    };
    updateConnection();
    window.addEventListener("offline", updateConnection);
    return () => {
      window.removeEventListener("offline", updateConnection);
    };
  }, []);

  const expireSession = useCallback(() => {
    setAuthState("error");
    setAuthMessage("Your Telegram session expired. Close and reopen PriceOn.");
  }, []);

  const loadLatestPrice = useCallback(async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/prices/latest`, { cache: "no-store" });
      if (!response.ok) throw new Error("latest price unavailable");
      const payload = (await response.json()) as { price?: string };
      const price = Number(payload.price);
      if (!Number.isFinite(price) || price <= 0) throw new Error("invalid latest price");
      lastPriceReceivedAtRef.current = Date.now();
      setLivePrice(price);
      setConnection("online");
    } catch {
      const hasRecentPrice =
        lastPriceReceivedAtRef.current > 0 && Date.now() - lastPriceReceivedAtRef.current <= 30_000;
      if (navigator.onLine && hasRecentPrice) return;
      setConnection(navigator.onLine ? "server-unavailable" : "device-offline");
    }
  }, []);

  const loadDemoAccount = useCallback(async () => {
    if (!authorization) return;
    setDemoStatus("loading");
    try {
      const endpoint = gameMode === "live"
        ? (gameModes?.live.network === "devnet" ? "/devnet/account" : "/mainnet/account")
        : "/demo/account";
      let response = await fetch(`${apiBaseUrl}${endpoint}`, { headers: { Authorization: authorization }, cache: "no-store" });
      if (response.status === 404) {
        response = await fetch(`${apiBaseUrl}${endpoint}`, { method: "PUT", headers: { Authorization: authorization } });
      }
      if (response.status === 401) { expireSession(); return; }
      if (!response.ok) throw new Error("demo account unavailable");
      const account = (await response.json()) as DemoAccountView;
      setDemoAccount(account);
      setDemoStatus("ready");

      // Show tutorial only if backend says pending AND we haven't shown it this session
      if (account.onboardingStatus === "pending") {
        const tutorialShownKey = "priceon:onboarding-tutorial-shown";
        let alreadyShown = tutorialShownThisSession; // Use state as default
        try {
          alreadyShown = typeof window !== "undefined" && localStorage.getItem(tutorialShownKey) === "true";
          if (!alreadyShown) {
            localStorage.setItem(tutorialShownKey, "true");
            setTutorialShownThisSession(true);
          }
        } catch {
          // localStorage unavailable (private mode, quota exceeded, etc)
          // Fall back to state variable tracking
          if (!tutorialShownThisSession) {
            setTutorialShownThisSession(true);
          } else {
            alreadyShown = true;
          }
        }
        // Only show tutorial if backend says pending AND we haven't shown it this session
        if (!alreadyShown) {
          setTutorialOpen(true);
        }
      }
    } catch { setDemoStatus("error"); }
  }, [authorization, expireSession, gameMode, gameModes?.live.network]);

  const loadWallet = useCallback(async () => {
    if (!authorization) return;
    setWalletStatus("loading");
    try {
      const headers = { Authorization: authorization };
      const [walletResponse, depositsResponse, withdrawalsResponse] = await Promise.all([
        fetch(`${apiBaseUrl}/wallet`, { headers, cache: "no-store" }),
        fetch(`${apiBaseUrl}/wallet/deposits?limit=5`, { headers, cache: "no-store" }),
        fetch(`${apiBaseUrl}/wallet/withdrawals?limit=5`, { headers, cache: "no-store" }),
      ]);
      if ([walletResponse.status, depositsResponse.status, withdrawalsResponse.status].some((status) => status === 401 || status === 403)) {
        expireSession();
        return;
      }
      if (!walletResponse.ok || !depositsResponse.ok || !withdrawalsResponse.ok) throw new Error("wallet unavailable");
      setWallet((await walletResponse.json()) as SolanaWalletResponse);
      setWalletDeposits((await depositsResponse.json()) as SolanaDepositHistoryResponse);
      const nextWithdrawals = (await withdrawalsResponse.json()) as SolanaWithdrawalHistoryResponse;
      walletWithdrawalsRef.current = nextWithdrawals;
      setWalletWithdrawals(nextWithdrawals);
      setWalletBalanceRefreshPending(false);
      setWalletStatus("ready");
    } catch {
      setWalletStatus("error");
    }
  }, [authorization, expireSession]);

  const refreshWalletBalance = useCallback((): Promise<boolean> => {
    if (!authorization) return Promise.resolve(false);
    if (walletBalanceRefreshRef.current) return walletBalanceRefreshRef.current;

    let request: Promise<boolean>;
    request = (async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/wallet`, {
          headers: { Authorization: authorization },
          cache: "no-store",
        });
        if (response.status === 401) {
          expireSession();
          return false;
        }
        if (!response.ok) return false;
        setWallet((await response.json()) as SolanaWalletResponse);
        setWalletBalanceRefreshPending(false);
        return true;
      } catch {
        return false;
      }
    })().finally(() => {
      if (walletBalanceRefreshRef.current === request) walletBalanceRefreshRef.current = undefined;
    });
    walletBalanceRefreshRef.current = request;
    return request;
  }, [authorization, expireSession]);

  const refreshWithdrawalState = useCallback((): Promise<void> => {
    if (!authorization) return Promise.resolve();
    if (withdrawalRefreshRef.current) return withdrawalRefreshRef.current;

    let request: Promise<void>;
    request = (async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/wallet/withdrawals?limit=5`, {
          headers: { Authorization: authorization },
          cache: "no-store",
        });
        if (response.status === 401) {
          expireSession();
          return;
        }
        if (!response.ok) return;
        const nextWithdrawals = (await response.json()) as SolanaWithdrawalHistoryResponse;
        const reachedTerminal = hasTerminalWithdrawalTransition(
          walletWithdrawalsRef.current?.items,
          nextWithdrawals.items,
        );
        walletWithdrawalsRef.current = nextWithdrawals;
        setWalletWithdrawals(nextWithdrawals);
        if (reachedTerminal) {
          setWalletBalanceRefreshPending(true);
          void refreshWalletBalance();
        }
      } catch {
        // Preserve the last known status; the next poll or foreground sync retries.
      }
    })().finally(() => {
      if (withdrawalRefreshRef.current === request) withdrawalRefreshRef.current = undefined;
    });
    withdrawalRefreshRef.current = request;
    return request;
  }, [authorization, expireSession, refreshWalletBalance]);

  const loadGameModes = useCallback(async () => {
    if (!authorization) return;
    try {
      const response = await fetch(`${apiBaseUrl}/game/modes`, {
        headers: { Authorization: authorization },
        cache: "no-store",
      });
      if (response.status === 401) {
        expireSession();
        return;
      }
      if (!response.ok) throw new Error("game modes unavailable");
      const payload = (await response.json()) as GameModesResponse;
      const initialized = gameModeInitializedRef.current;
      gameModeInitializedRef.current = true;
      setGameModes(payload);
      setGameMode((current) => resolveGameModeAfterModesLoad(current, initialized, payload));
    } catch {
      // Preserve the current mode when a background refresh cannot reach the API.
    }
  }, [authorization, expireSession]);

  const loadReferral = useCallback(async () => {
    if (!authorization) return;
    try {
      const response = await fetch(`${apiBaseUrl}/referrals/me`, { headers: { Authorization: authorization }, cache: "no-store" });
      if (response.status === 401) { expireSession(); return; }
      if (!response.ok) throw new Error("referrals unavailable");
      setReferral((await response.json()) as ReferralMeResponse);
    } catch { setReferralMessage("Referral details are temporarily unavailable."); }
  }, [authorization, expireSession]);

  const activateReferralCode = useCallback(async (): Promise<void> => {
    if (!authorization || !referralCode.trim()) return;
    setReferralSubmitting(true);
    setReferralMessage(undefined);
    try {
      const response = await fetch(`${apiBaseUrl}/referrals/activate`, {
        method: "POST", headers: { Authorization: authorization, "Content-Type": "application/json" },
        body: JSON.stringify({ code: referralCode.trim() }),
      });
      const payload = await response.json() as ReferralMeResponse | { error?: { message?: string } };
      if (!response.ok) { setReferralMessage((payload as { error?: { message?: string } }).error?.message || "Referral activation failed."); return; }
      setReferral(payload as ReferralMeResponse);
      setReferralCode("");
      setReferralMessage("Live access is active.");
      await Promise.all([loadGameModes(), loadWallet()]);
    } catch { setReferralMessage("Referral activation is temporarily unavailable."); }
    finally { setReferralSubmitting(false); }
  }, [authorization, loadGameModes, loadWallet, referralCode]);

  const submitWithdrawal = useCallback(async (
    destinationAddress: string,
    amountSol: string,
    idempotencyKey: string,
  ): Promise<string | undefined> => {
    if (!authorization) return "Your Telegram session expired. Close and reopen PriceOn.";
    try {
      const response = await fetch(`${apiBaseUrl}/wallet/withdrawals`, {
        method: "POST",
        headers: {
          Authorization: authorization,
          "Content-Type": "application/json",
          "Idempotency-Key": idempotencyKey,
        },
        body: JSON.stringify({ destinationAddress, amountSol }),
      });
      const payload = await response.json() as SolanaWithdrawalResponse | { error?: { message?: string } };
      if (response.status === 401) {
        expireSession();
        return "Your Telegram session expired. Close and reopen PriceOn.";
      }
      if (!response.ok) {
        return (payload as { error?: { message?: string } }).error?.message || "The withdrawal could not be submitted.";
      }
      const result = payload as SolanaWithdrawalResponse;
      setWallet({ wallet: result.wallet, serverTime: result.serverTime });
      setWalletWithdrawals((current) => ({
        items: [result.withdrawal, ...(current?.items ?? []).filter((item) => item.id !== result.withdrawal.id)].slice(0, 5),
        nextCursor: current?.nextCursor ?? null,
        serverTime: result.serverTime,
      }));
      return undefined;
    } catch {
      return "The withdrawal service could not be reached. Submit again with the same confirmation.";
    }
  }, [authorization, expireSession]);

  const cancelWithdrawal = useCallback(async (withdrawalId: string): Promise<string | undefined> => {
    if (!authorization) return "Your Telegram session expired. Close and reopen PriceOn.";
    try {
      const response = await fetch(`${apiBaseUrl}/wallet/withdrawals/${withdrawalId}`, {
        method: "DELETE",
        headers: { Authorization: authorization },
      });
      const payload = await response.json() as { withdrawal?: SolanaWithdrawalHistoryResponse["items"][number]; error?: { message?: string } };
      if (response.status === 401) {
        expireSession();
        return "Your Telegram session expired. Close and reopen PriceOn.";
      }
      if (!response.ok) return payload.error?.message || "The withdrawal could not be cancelled.";
      if (payload.withdrawal) {
        setWalletWithdrawals((current) => current ? {
          ...current,
          items: current.items.map((item) => item.id === withdrawalId ? payload.withdrawal! : item),
        } : current);
      }
      await refreshWalletBalance();
      return undefined;
    } catch {
      return "The withdrawal service could not be reached.";
    }
  }, [authorization, expireSession, refreshWalletBalance]);

  const loadHistory = useCallback(async (cursor?: string) => {
    if (!authorization) return;
    const requestId = ++historyRequestRef.current;
    setHistoryStatus("loading");
    const query = new URLSearchParams({ limit: "20" });
    if (cursor) query.set("cursor", cursor);
    try {
      const response = await fetch(`${apiBaseUrl}/demo/history?${query}`, {
        headers: { Authorization: authorization },
        cache: "no-store",
      });
      if (response.status === 401) {
        expireSession();
        return;
      }
      if (!response.ok) throw new Error("history unavailable");
      const payload = (await response.json()) as DemoHistoryResponse;
      if (requestId !== historyRequestRef.current) return;
      setHistory((current) => cursor ? [...current, ...payload.items] : payload.items);
      setHistoryCursor(payload.nextCursor);
      setHistoryStatus("ready");
    } catch {
      if (requestId !== historyRequestRef.current) return;
      setHistoryStatus("error");
    }
  }, [authorization, expireSession]);

  const loadLiveHistory = useCallback(async (cursor?: string) => {
    if (!authorization || !gameModes?.live.invited) return;
    setLiveHistoryStatus("loading");
    const query = new URLSearchParams({ limit: "20" });
    if (cursor) query.set("cursor", cursor);
    try {
      const response = await fetch(`${apiBaseUrl}/live/history?${query}`, {
        headers: { Authorization: authorization },
        cache: "no-store",
      });
      if (response.status === 401) {
        expireSession();
        return;
      }
      if (!response.ok) throw new Error("live history unavailable");
      const payload = (await response.json()) as LiveHistoryResponse;
      const records = payload.items.map(toUnifiedLiveHistoryItem);
      setLiveHistory((current) => cursor ? [...current, ...records] : records);
      setLiveHistoryCursor(payload.nextCursor);
      setLiveHistoryStatus("ready");
    } catch {
      setLiveHistoryStatus("error");
    }
  }, [authorization, expireSession, gameModes?.live.invited]);

  const loadCompetitionDetail = useCallback(async (competitionId: string) => {
    if (!authorization || !areBattlesEnabled(gameModes)) return;
    try {
      let network = gameMode === "demo" ? "demo" : (gameModes?.live.invited ? (gameModes?.live.network || "demo") : "demo");
      // Convert mainnet-beta to mainnet for competitions API
      if (network === "mainnet-beta") network = "mainnet";
      const response = await fetch(`${apiBaseUrl}/competitions/${competitionId}?network=${encodeURIComponent(network || "demo")}`, {
        headers: { Authorization: authorization },
        cache: "no-store",
      });
      if (response.status === 401) { expireSession(); return; }
      if (!response.ok) throw new Error("competition unavailable");
      const detail = (await response.json()) as CompetitionPoolDetail;
      if (selectedCompetitionRef.current === competitionId) {
        setCompetitionDetail(detail);
        const terminalEntry = detail.myEntry?.status === "settled" || detail.myEntry?.status === "refunded";
        const accountSyncKey = terminalEntry
          ? `${detail.id}:${detail.myEntry?.status}:${detail.myEntry?.payoutSol}`
          : undefined;
        if (accountSyncKey && competitionAccountSyncRef.current !== accountSyncKey) {
          competitionAccountSyncRef.current = accountSyncKey;
          void loadDemoAccount();
        }
      }
    } catch {
      if (selectedCompetitionRef.current === competitionId) {
        setCompetitionAction("Competition details are temporarily unavailable.");
      }
    }
  }, [authorization, expireSession, loadDemoAccount, gameMode, gameModes]);

  const loadCompetitions = useCallback(async () => {
    if (!authorization || !areBattlesEnabled(gameModes)) return;
    setCompetitionStatus((current) => current === "ready" ? current : "loading");
    try {
      let network = gameMode === "demo" ? "demo" : (gameModes?.live.invited ? (gameModes?.live.network || "demo") : "demo");
      // Convert mainnet-beta to mainnet for competitions API
      if (network === "mainnet-beta") network = "mainnet";
      const response = await fetch(`${apiBaseUrl}/competitions?network=${encodeURIComponent(network || "demo")}`, {
        headers: { Authorization: authorization },
        cache: "no-store",
      });
      if (response.status === 401) { expireSession(); return; }
      if (!response.ok) throw new Error("competitions unavailable");
      const dashboard = (await response.json()) as CompetitionDashboardResponse;
      setCompetitionDashboard(dashboard);
      setCompetitionStatus("ready");
      const startId = getCompetitionStartId();
      const currentId = selectedCompetitionRef.current;
      const preferred = startId
        || dashboard.myCompetitions.find((competition) => competition.status === "active")?.id
        || dashboard.myCompetitions[0]?.id;
      if (!currentId && preferred) {
        selectedCompetitionRef.current = preferred;
        setSelectedCompetitionId(preferred);
      }
    } catch {
      setCompetitionStatus("error");
    }
  }, [authorization, expireSession, gameMode, gameModes]);

  const loadPrediction = useCallback(async (roundId: string, header: string) => {
    const response = await fetch(`${apiBaseUrl}/predictions/${roundId}`, {
      headers: { Authorization: header },
    });
    if (response.status === 404) {
      // Mark as "no prediction exists" to avoid refetching on next refresh
      setPredictions((current) => ({ ...current, [roundId]: null as any }));
      return;
    }
    if (response.status === 401) {
      expireSession();
      return;
    }
    if (!response.ok) return;
    const prediction = (await response.json()) as PredictionView;
    setPredictions((current) => ({ ...current, [prediction.roundId]: prediction }));
  }, [expireSession]);

  const loadTrade = useCallback(async (roundId: string, mode: GameMode) => {
    const response = await fetch(`${apiBaseUrl}${mode === "live" ? "/live/trades" : "/demo/trades"}/${roundId}`, {
      headers: { Authorization: authorization! },
      cache: "no-store",
    });
    if (response.ok) {
      const trade = (await response.json()) as DemoTradeView | LiveTradeView;
      if (mode === "live") setLiveTrades((current) => ({ ...current, [trade.roundId]: trade as LiveTradeView }));
      else setDemoTrades((current) => ({ ...current, [trade.roundId]: trade as DemoTradeView }));
      return;
    }
    if (mode === "live") setLiveTrades((current) => ({ ...current, [roundId]: null as any }));
    else setDemoTrades((current) => ({ ...current, [roundId]: null as any }));
  }, [authorization]);

  const refreshRoundState = useCallback(async () => {
    if (!authorization) return;
    const requestId = ++roundRequestRef.current;
    try {
      const roundNetwork = gameMode === "live" && gameModes?.live.invited
        ? gameModes.live.network
        : "demo";
      const response = await fetch(`${apiBaseUrl}/rounds/state?network=${encodeURIComponent(roundNetwork)}`, { cache: "no-store" });
      if (!response.ok) throw new Error("round state unavailable");
      const payload = (await response.json()) as RoundStateResponse;
      if (requestId !== roundRequestRef.current) return;
      const receivedAt = Date.now();
      const nextOffset = getServerOffset(payload.serverTime, receivedAt);
      serverOffsetRef.current = nextOffset;
      setServerOffset(nextOffset);
      setRoundState(payload);
      roundStateRef.current = payload;
      setRoundStateSettled(true);
      setRoundMessage(
        payload.current || payload.upcoming || payload.previous
          ? ""
          : "Waiting for the round engine to open a fresh round...",
      );

      // Only fetch trades for the current round (user can only interact with current round)
      const currentRoundId = payload.current?.id;
      if (currentRoundId) {
        const currentRoundClosed = payload.current?.status === "closed" || payload.current?.status === "cancelled";
        
        // Only fetch prediction if not already fetched (undefined = not fetched, null = no prediction exists)
        // OR if round just closed and we need to get the settled outcome
        if (predictions[currentRoundId] === undefined || (currentRoundClosed && predictions[currentRoundId]?.outcome === "pending")) {
          await loadPrediction(currentRoundId, authorization);
        }
        // Only fetch demo trade if not already fetched (undefined = not fetched, null = no trade exists)
        // OR if round just closed and trade is still pending
        if (demoTrades[currentRoundId] === undefined || (currentRoundClosed && demoTrades[currentRoundId]?.status === "pending")) {
          await loadTrade(currentRoundId, "demo");
        }
        if (gameModes?.live.invited) {
          // Only fetch live trade if not already fetched OR if round closed and trade pending
          if (liveTrades[currentRoundId] === undefined || (currentRoundClosed && liveTrades[currentRoundId]?.status === "pending")) {
            await loadTrade(currentRoundId, "live");
          }
        }
      }
      if (payload.previous?.id && payload.previous.id !== previousRoundRef.current) {
        const previousRoundId = payload.previous.id;
        previousRoundRef.current = previousRoundId;
        await Promise.all([
          loadPrediction(previousRoundId, authorization),
          loadTrade(previousRoundId, "demo"),
          gameModes?.live.invited ? loadTrade(previousRoundId, "live") : Promise.resolve(),
          loadDemoAccount(),
          loadHistory(),
          loadGameModes(),
          loadLiveHistory(),
        ]);
      }
    } catch {
      if (requestId !== roundRequestRef.current) return;
      setRoundMessage("Round state is temporarily unavailable.");
      setRoundStateSettled(true);
    }
  }, [authorization, gameMode, gameModes?.live.invited, gameModes?.live.network, loadDemoAccount, loadGameModes, loadHistory, loadLiveHistory, loadPrediction, loadTrade]);

  useEffect(() => {
    if (authState === "authenticated" && authorization) {
      void loadDemoAccount();
      void loadGameModes();
      void loadReferral();
    }
  }, [authState, authorization, loadDemoAccount, loadGameModes, loadReferral]);

  useEffect(() => {
    if (authState === "authenticated") void loadLatestPrice();
  }, [authState, loadLatestPrice]);

  useEffect(() => {
    if (
      authState === "authenticated" &&
      activeTab === "stats"
    ) {
      if (gameMode === "live") void loadLiveHistory();
      else void loadHistory();
    }
  }, [activeTab, authState, gameMode, loadHistory, loadLiveHistory]);

  useEffect(() => {
    if (
      authState === "authenticated" &&
      activeTab === "stats" && demoStatus === "idle"
    ) void loadDemoAccount();
  }, [activeTab, authState, demoStatus, loadDemoAccount]);

  useEffect(() => {
    if (authState === "authenticated" && activeTab === "profile") {
      void loadWallet();
    }
  }, [activeTab, authState, loadWallet]);

  const hasActiveWalletWithdrawal = hasActiveWithdrawals(walletWithdrawals?.items);

  useEffect(() => {
    if (
      authState !== "authenticated"
      || !authorization
      || activeTab !== "profile"
      || (!hasActiveWalletWithdrawal && !walletBalanceRefreshPending)
    ) return;
    let cancelled = false;
    let timer: number | undefined;

    async function cycle() {
      if (document.visibilityState === "visible" && navigator.onLine) {
        if (hasActiveWalletWithdrawal) await refreshWithdrawalState();
        if (walletBalanceRefreshPending) await refreshWalletBalance();
      }
      if (!cancelled) timer = window.setTimeout(cycle, withdrawalPollIntervalMs);
    }

    timer = window.setTimeout(cycle, withdrawalPollIntervalMs);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [
    activeTab,
    authState,
    authorization,
    hasActiveWalletWithdrawal,
    refreshWalletBalance,
    refreshWithdrawalState,
    walletBalanceRefreshPending,
  ]);

  useEffect(() => {
    if (authState !== "authenticated" || !authorization || !battlesEnabled) return;
    void loadCompetitions();
    const timer = window.setInterval(
      () => void loadCompetitions(),
      activeTab === "battle" ? 2_000 : 5_000,
    );
    return () => window.clearInterval(timer);
  }, [activeTab, authState, authorization, battlesEnabled, loadCompetitions]);

  useEffect(() => {
    if (authState !== "authenticated" || !authorization) return;
    let stopped = false;

    const sendPresence = (visible: boolean, keepalive = false) => {
      if (stopped && visible) return;
      void fetch(`${apiBaseUrl}/users/me/presence`, {
        method: "POST",
        headers: { Authorization: authorization, "Content-Type": "application/json" },
        body: JSON.stringify({ visible }),
        keepalive,
      }).catch(() => undefined);
    };
    const reportVisibility = () => sendPresence(document.visibilityState === "visible", true);
    const reportHidden = () => sendPresence(false, true);

    reportVisibility();
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") sendPresence(true);
    }, 10_000);
    document.addEventListener("visibilitychange", reportVisibility);
    window.addEventListener("pagehide", reportHidden);
    return () => {
      stopped = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", reportVisibility);
      window.removeEventListener("pagehide", reportHidden);
      sendPresence(false, true);
    };
  }, [authState, authorization]);

  useEffect(() => {
    if (!battlesEnabled) {
      setBattleAlert(undefined);
      return;
    }
    const candidate = competitionDashboard?.myCompetitions.find((competition) => {
      const startsAt = Date.parse(competition.startsAt);
      return competition.status === "active" && startsAt > now && startsAt - now <= PREDICTION_LEAD_MS;
    });
    if (!candidate) {
      setBattleAlert(undefined);
      return;
    }
    if (activeTab === "battle" && selectedCompetitionId === candidate.id) {
      markBattleAlertHandled(candidate.id);
      setBattleAlert(undefined);
      return;
    }
    if (!wasBattleAlertHandled(candidate.id)) setBattleAlert(candidate);
  }, [activeTab, battlesEnabled, competitionDashboard, now, selectedCompetitionId]);

  useEffect(() => {
    if (!battlesEnabled) {
      if (activeTab === "battle") setActiveTab("play");
      return;
    }
    if (authState !== "authenticated" || battleStartHandledRef.current) return;
    const competitionId = getCompetitionStartId();
    if (!competitionId) return;
    battleStartHandledRef.current = true;
    selectedCompetitionRef.current = competitionId;
    setSelectedCompetitionId(competitionId);
    setActiveTab("battle");
  }, [activeTab, authState, battlesEnabled]);

  useEffect(() => {
    if (authState !== "authenticated" || !battlesEnabled || !selectedCompetitionId) return;
    setCompetitionAction(undefined);
    void loadCompetitionDetail(selectedCompetitionId);
  }, [authState, battlesEnabled, loadCompetitionDetail, selectedCompetitionId]);

  useEffect(() => {
    if (authState !== "authenticated" || !battlesEnabled || !selectedCompetitionId) return;
    const timer = window.setInterval(
      () => void loadCompetitionDetail(selectedCompetitionId),
      activeTab === "battle" ? 2_000 : 5_000,
    );
    return () => window.clearInterval(timer);
  }, [activeTab, authState, battlesEnabled, loadCompetitionDetail, selectedCompetitionId]);

  useEffect(() => {
    if (authState !== "authenticated" || !authorization) return;
    let cancelled = false;
    let timer: number | undefined;

    async function cycle() {
      await refreshRoundState();
      if (cancelled) return;
      const state = roundStateRef.current;
      const serverNow = Date.now() + serverOffsetRef.current;
      timer = window.setTimeout(
        cycle,
        getNextRoundRefreshDelay(serverNow, state?.current, state?.upcoming),
      );
    }

    void cycle();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [authState, authorization, refreshRoundState]);

  useEffect(() => {
    if (authState !== "authenticated") return;
    let cancelled = false;

    async function loadCurrentSeries() {
      setPriceSeriesStatus((current) => current === "ready" ? current : "loading");
      try {
        const response = await fetch(
          `${apiBaseUrl}/prices/series?from=${encodeURIComponent(new Date(Date.now() + serverOffset - 9 * 60_000).toISOString())}`,
          { cache: "no-store" },
        );
        if (!response.ok) {
          if (!cancelled) setPriceSeriesStatus((current) => current === "ready" ? current : "error");
          return;
        }
        const payload = (await response.json()) as PriceSeriesResponse;
        if (cancelled) return;
        const points = payload.points
          .map((point) => ({ timestamp: Date.parse(point.observedAt), price: Number(point.price) }))
          .filter((point) => Number.isFinite(point.timestamp) && Number.isFinite(point.price));
        setPriceSeries(points);
        setPriceSeriesStatus("ready");
      } catch {
        if (!cancelled) setPriceSeriesStatus((current) => current === "ready" ? current : "error");
      }
    }

    void loadCurrentSeries();
    return () => {
      cancelled = true;
    };
  }, [activeRound?.id, authState, seriesRefreshToken, serverOffset]);

  useEffect(() => {
    if (authState !== "authenticated") return;
    let cancelled = false;
    let socket: WebSocket | undefined;
    let reconnectTimer: number | undefined;
    let heartbeatTimer: number | undefined;
    let reconnectAttempt = 0;
    let lastStaleProbeAt = 0;
    const intentionalCloses = new WeakSet<WebSocket>();

    function clearReconnectTimer() {
      if (!reconnectTimer) return;
      window.clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    }

    function clearHeartbeatTimer() {
      if (!heartbeatTimer) return;
      window.clearInterval(heartbeatTimer);
      heartbeatTimer = undefined;
    }

    function closeCurrentSocket() {
      const currentSocket = socket;
      socket = undefined;
      if (!currentSocket) return;
      intentionalCloses.add(currentSocket);
      clearHeartbeatTimer();
      currentSocket.close();
    }

    function scheduleReconnect() {
      if (cancelled || document.visibilityState === "hidden" || reconnectTimer) return;
      const delay = Math.min(15_000, 1000 * 2 ** reconnectAttempt);
      reconnectAttempt += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = undefined;
        connect();
      }, delay);
    }

    function sendPing(ws: WebSocket) {
      if (ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({ type: "ping" }));
        } catch {
          // Ignore send errors, connection will be detected as stale
        }
      }
    }

    function connect() {
      if (cancelled || document.visibilityState === "hidden") return;
      if (!navigator.onLine) {
        setConnection("device-offline");
        return;
      }
      if (socket && (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.OPEN)) return;
      setConnection("connecting");
      const currentSocket = new WebSocket(getPriceStreamUrl());
      socket = currentSocket;
      currentSocket.onmessage = (event) => {
        let message: PriceStreamMessage;
        try {
          message = JSON.parse(String(event.data)) as PriceStreamMessage;
        } catch {
          return;
        }
        if (message.type === "pong") {
          return;
        }
        if (message.type !== "price") {
          setConnection(navigator.onLine ? "server-unavailable" : "device-offline");
          void loadLatestPrice();
          return;
        }
        const price = Number(message.data.price);
        const timestamp = Date.parse(message.data.observedAt);
        if (!Number.isFinite(price) || price <= 0 || !Number.isFinite(timestamp)) return;
        lastPriceReceivedAtRef.current = Date.now();
        reconnectAttempt = 0;
        setLivePrice(price);
        setConnection("online");
        const round = activeRoundRef.current;
        if (round) {
          setPriceSeries((current) => appendLivePricePoint(current, { timestamp, price }, round));
        }
      };
      currentSocket.onerror = () => currentSocket.close();
      currentSocket.onopen = () => {
        clearHeartbeatTimer();
        heartbeatTimer = window.setInterval(() => sendPing(currentSocket), 20_000);
      };
      currentSocket.onclose = () => {
        if (socket !== currentSocket) return;
        socket = undefined;
        clearHeartbeatTimer();
        if (intentionalCloses.has(currentSocket)) return;
        setConnection(navigator.onLine ? "reconnecting" : "device-offline");
        scheduleReconnect();
      };
    }

    function pause() {
      clearReconnectTimer();
      closeCurrentSocket();
    }

    function reconnectNow() {
      if (cancelled) return;
      clearReconnectTimer();
      closeCurrentSocket();
      reconnectAttempt = 0;
      setConnection(navigator.onLine ? "connecting" : "device-offline");
      connect();
    }

    priceReconnectRef.current = reconnectNow;
    pricePauseRef.current = pause;
    lastPriceReceivedAtRef.current = Date.now();
    connect();
    const staleTimer = window.setInterval(() => {
      const timestamp = Date.now();
      if (
        document.visibilityState !== "hidden" &&
        timestamp - lastPriceReceivedAtRef.current > 30_000
      ) {
        setConnection(navigator.onLine ? "server-unavailable" : "device-offline");
        if (timestamp - lastStaleProbeAt > 5_000) {
          lastStaleProbeAt = timestamp;
          void loadLatestPrice();
        }
      }
    }, 1000);
    return () => {
      cancelled = true;
      priceReconnectRef.current = () => undefined;
      pricePauseRef.current = () => undefined;
      clearReconnectTimer();
      closeCurrentSocket();
      window.clearInterval(staleTimer);
    };
  }, [authState, loadLatestPrice]);

  const closeBattleDetail = useCallback(() => {
    setSelectedCompetitionId(undefined);
    setCompetitionDetail(undefined);
  }, []);

  const syncAfterResume = useCallback(() => {
    if (authState !== "authenticated" || !authorization) return;
    const timestamp = Date.now();
    if (timestamp - lastResumeSyncAtRef.current < 750) return;
    lastResumeSyncAtRef.current = timestamp;
    setNow(timestamp + serverOffsetRef.current);
    setSeriesRefreshToken((current) => current + 1);
    void loadLatestPrice();
    void loadDemoAccount();
    void loadGameModes();
    priceReconnectRef.current();
    void refreshRoundState();
    if (activeTab === "stats" && gameMode === "live") {
      void loadLiveHistory();
    } else if (activeTab === "stats" || activeTab === "profile" || historyStatus !== "idle") {
      void loadHistory();
    }
    if (activeTab === "profile") {
      if (hasActiveWithdrawals(walletWithdrawalsRef.current?.items)) void refreshWithdrawalState();
      if (walletBalanceRefreshPending) void refreshWalletBalance();
    }
    if (battlesEnabled) {
      void loadCompetitions();
      if (selectedCompetitionRef.current) void loadCompetitionDetail(selectedCompetitionRef.current);
    }
  }, [activeTab, authState, authorization, battlesEnabled, gameMode, historyStatus, loadCompetitionDetail, loadCompetitions, loadDemoAccount, loadGameModes, loadHistory, loadLatestPrice, loadLiveHistory, refreshRoundState, refreshWalletBalance, refreshWithdrawalState, walletBalanceRefreshPending]);

  useEffect(() => {
    if (authState !== "authenticated" || !authorization) return;
    resumePendingRef.current = document.visibilityState === "hidden";

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        resumePendingRef.current = true;
        pricePauseRef.current();
        return;
      }
      if (!resumePendingRef.current) return;
      resumePendingRef.current = false;
      syncAfterResume();
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      resumePendingRef.current = false;
      syncAfterResume();
    };
    const handleOffline = () => {
      setConnection("device-offline");
      pricePauseRef.current();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("online", syncAfterResume);
    window.addEventListener("offline", handleOffline);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("online", syncAfterResume);
      window.removeEventListener("offline", handleOffline);
    };
  }, [authState, authorization, syncAfterResume]);

  useEffect(() => {
    let cancelled = false;
    const telegram = window.Telegram?.WebApp;
    telegram?.ready();
    telegram?.expand();
    telegram?.setHeaderColor?.("#090b12");
    telegram?.setBackgroundColor?.("#090b12");

    async function authenticate() {
      try {
        const authorization = telegram?.initData ? `tma ${telegram.initData}` : "dev";
        const response = await fetch(`${apiBaseUrl}/auth/me`, {
          headers: { Authorization: authorization },
        });
        const payload = (await response.json()) as AuthenticatedUser | AuthErrorResponse;
        if (cancelled) return;

        if (response.ok) {
          setAuthorization(authorization);
          setUser(payload as AuthenticatedUser);
          setAuthState("authenticated");
          return;
        }

        const error = (payload as AuthErrorResponse).error;
        if (error?.code === "USER_NOT_REGISTERED") {
          setAuthState("registration-required");
          setAuthMessage("Start the PriceOn bot once to create your player profile.");
          return;
        }
        setAuthState("error");
        setAuthMessage(
          error?.code === "AUTH_EXPIRED"
            ? "Your Telegram session expired. Close and reopen PriceOn."
            : error?.message || "We could not verify your Telegram session.",
        );
      } catch {
        if (!cancelled) {
          setAuthState("error");
          setAuthMessage("PriceOn could not reach the authentication service.");
        }
      }
    }

    void authenticate();
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    const telegram = window.Telegram?.WebApp;
    telegram?.ready();
    telegram?.expand();
    telegram?.setHeaderColor?.("#090b12");
    telegram?.setBackgroundColor?.("#090b12");
    const root = document.documentElement;
    const existing = parseFloat(root.style.getPropertyValue("--app-boot-viewport-height")) || 0;
    if (existing <= 0) {
      const locked = lockMiniAppBootViewportHeight(existing, resolveMiniAppBootViewportHeight({
        innerHeight: window.innerHeight,
        screenHeight: window.screen?.height ?? 0,
        coarsePointer: window.matchMedia?.("(pointer: coarse)").matches,
        isTelegram: Boolean(telegram),
        isExpanded: telegram?.isExpanded,
        viewportHeight: telegram?.viewportHeight,
        viewportStableHeight: telegram?.viewportStableHeight,
      }));
      if (locked > 0) root.style.setProperty("--app-boot-viewport-height", `${locked}px`);
    }
    if (!telegram) {
      setViewportStable(true);
      return;
    }
    if (telegram.isExpanded) setViewportStable(true);
    const handleViewportChanged = (...args: unknown[]) => {
      const event = args[0] as { isStateStable?: boolean } | undefined;
      if (event?.isStateStable) setViewportStable(true);
    };
    telegram.onEvent?.("viewportChanged", handleViewportChanged);
    const timeout = window.setTimeout(() => setViewportStable(true), MINI_APP_VIEWPORT_STABLE_TIMEOUT_MS);
    return () => {
      telegram.offEvent?.("viewportChanged", handleViewportChanged);
      window.clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (authState !== "authenticated") return;
    const timeout = window.setTimeout(() => setPlayBootTimedOut(true), MINI_APP_PLAY_BOOT_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [authState]);

  useEffect(() => {
    if (authState !== "authenticated") return;
    const timer = window.setInterval(() => {
      setNow(Date.now() + serverOffset);
    }, 250);

    return () => window.clearInterval(timer);
  }, [authState, serverOffset]);

  async function submitPrediction(selected: Direction) {
    setPendingDirection(selected);
    const existingTrade = gameMode === "live" ? upcomingLiveTrade : upcomingDemoTrade;
    if (
      !authorization ||
      !upcomingRound ||
      existingTrade ||
      submitting ||
      !isPredictionWindowOpen(now, upcomingRound) ||
      (gameMode === "demo" && !demoAccount) ||
      (gameMode === "live" && !gameModes?.live.enabled)
    ) return;
    setSubmitting(true);
    setPredictionMessage(undefined);
    try {
      if (gameMode === "live") {
        const response = await fetch(`${apiBaseUrl}/live/trades`, {
          method: "POST",
          headers: {
            Authorization: authorization,
            "Content-Type": "application/json",
            "Idempotency-Key": createIdempotencyKey(),
          },
          body: JSON.stringify({ roundId: upcomingRound.id, direction: selected, stakeSol }),
        });
        const payload = (await response.json()) as LiveTradeResponse | { error?: { message?: string } };
        if (response.ok) {
          const result = payload as LiveTradeResponse;
          setLiveTrades((current) => ({ ...current, [result.trade.roundId]: result.trade }));
          triggerHaptic("locked");
          setGameModes((current) => current ? {
            ...current,
            live: { ...current.live, balance: result.balance },
            serverTime: result.serverTime,
          } : current);
          void loadLiveHistory();
          return;
          setPendingDirection(undefined);
        }
        const error = (payload as { error?: { message?: string } }).error;
        setPredictionMessage(error?.message || "The live trade could not be submitted.");
        if (response.status === 401) expireSession();
        else if (response.status === 409) await refreshRoundState();
      } else {
        const response = await fetch(`${apiBaseUrl}/demo/trades`, {
          method: "POST",
          headers: { Authorization: authorization, "Content-Type": "application/json" },
          body: JSON.stringify({ roundId: upcomingRound.id, direction: selected, stakeSol }),
        });
        const payload = (await response.json()) as { trade: DemoTradeView; account: DemoAccountView } | { error?: { message?: string } };
        if (response.ok) {
          const result = payload as { trade: DemoTradeView; account: DemoAccountView };
          setDemoTrades((current) => ({ ...current, [result.trade.roundId]: result.trade }));
          triggerHaptic("locked");
          setDemoAccount(result.account);
          void loadHistory();
          return;
        }
        const error = (payload as { error?: { message?: string } }).error;
        setPredictionMessage(error?.message || "The prediction could not be submitted.");
        if (response.status === 401) expireSession();
        else if (response.status === 409) await refreshRoundState();
      }
    } catch {
      setPredictionMessage(gameMode === "live"
        ? "The live trading service could not be reached. Submit again before time expires."
        : "The prediction service could not be reached. Try again before time expires.");
    } finally {
      setSubmitting(false);
      setPendingDirection(undefined);
    }
  }

  function selectGameMode(nextMode: GameMode) {
    if (nextMode === gameMode) return;
    if (nextMode === "live") {
      if (!gameModes?.live.invited) return;
      if (!liveModeConfirmedRef.current) {
        const networkName = gameModes.live.network === "devnet" ? "Solana Devnet" : "Solana Mainnet";
        const confirmed = window.confirm(
          `${networkName} Live mode uses the SOL balance in your in-app wallet. Trades are final after each round. Continue?`,
        );
        if (!confirmed) return;
        liveModeConfirmedRef.current = true;
      }
      void loadLiveHistory();
    }
    setPredictionMessage(undefined);
    selectedCompetitionRef.current = undefined;
    setSelectedCompetitionId(undefined);
    setCompetitionDetail(undefined);
    setCompetitionAction(undefined);
    setCompetitionSubmitting(false);
    setGameMode(nextMode);
  }

  const playChartRound = resolvePlayChartRound(activeRound, previousRound);
  const playPrice = livePrice ?? activeRound?.startPrice ?? playChartRound?.closePrice ?? playChartRound?.startPrice;
  const playLineChart = Boolean(
    pendingDirection
    ?? (gameMode === "live" ? upcomingLiveTrade ?? currentLiveTrade : upcomingDemoTrade ?? currentDemoTrade)
    ?? currentPrediction?.direction,
  );
  const chartReady = priceSeriesStatus !== "loading" && canPaintPriceChart({
    price: playPrice,
    points: priceSeries,
    round: playChartRound,
    now,
    lineChart: playLineChart,
  });
  const revealPlay = shouldRevealMiniAppPlay({
    authState,
    alreadyRevealed: playRevealed,
    viewportStable,
    roundStateSettled,
    chartReady,
    bootTimedOut: playBootTimedOut,
  });

  useEffect(() => {
    if (revealPlay) setPlayRevealed(true);
  }, [revealPlay]);

  if (authState !== "authenticated" || !revealPlay) {
    return (
      <AuthGate
        state={authState === "authenticated" ? "checking" : authState}
        message={authMessage}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const price = livePrice ?? activeRound?.startPrice;
  const secondsRemaining = activeRound ? getSecondsRemaining(now, activeRound) : 0;
  const displayName = user?.firstName || (user?.username ? `@${user.username}` : "Player");
  const showModeSwitch = Boolean(gameModes?.live.invited);

  async function updateOnboarding(status: "skipped" | "completed") {
    if (!authorization) return;

    const tutorialShownKey = "priceon:onboarding-tutorial-shown";

    // Mark as shown in localStorage BEFORE API call
    try {
      localStorage.setItem(tutorialShownKey, "true");
    } catch {
      // Ignore storage errors
    }

    // Also mark in session state
    setTutorialShownThisSession(true);

    setTutorialOpen(false);
    const response = await fetch(`${apiBaseUrl}/demo/account/onboarding`, {
      method: "PATCH", headers: { Authorization: authorization, "Content-Type": "application/json" },
      body: JSON.stringify({ status }),
    });

    if (response.ok) {
      setDemoAccount((await response.json()) as DemoAccountView);
      setActiveTab("play");
      await refreshRoundState();
    } else {
      // API failed - revert both localStorage flag and session state so tutorial can show again if needed
      try {
        localStorage.removeItem(tutorialShownKey);
      } catch {
        // Ignore storage errors
      }
      setTutorialShownThisSession(false);
      // Re-open tutorial to show it wasn't properly saved
      setTutorialOpen(true);
    }
  }

  async function resetDemo() {
    if (!authorization || !demoAccount?.canReset) return;
    if (!window.confirm("Reset your Demo SOL balance to 10.00? Your trade history will be kept.")) return;
    const response = await fetch(`${apiBaseUrl}/demo/account/reset`, { method: "POST", headers: { Authorization: authorization } });
    if (response.ok) setDemoAccount((await response.json()) as DemoAccountView);
  }

  function selectCompetition(competitionId: string) {
    const changed = selectedCompetitionRef.current !== competitionId;
    selectedCompetitionRef.current = competitionId;
    setSelectedCompetitionId(competitionId);
    if (changed) setCompetitionDetail(undefined);
    setCompetitionAction(undefined);
    if (!changed) void loadCompetitionDetail(competitionId);
  }

  async function updateCompetitionEntry(method: "POST" | "DELETE", competitionId: string) {
    if (!authorization || competitionSubmitting) return;
    setCompetitionSubmitting(true);
    setCompetitionAction(undefined);
    try {
      let network = gameMode === "demo" ? "demo" : (gameModes?.live.invited ? (gameModes?.live.network || "demo") : "demo");
      // Convert mainnet-beta to mainnet for competitions API
      if (network === "mainnet-beta") network = "mainnet";
      const body = method === "POST" ? JSON.stringify({ network }) : undefined;
      const url = method === "DELETE"
        ? `${apiBaseUrl}/competitions/${competitionId}/entry?network=${network}`
        : `${apiBaseUrl}/competitions/${competitionId}/entry`;
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: authorization,
          ...(method === "POST" && { "Content-Type": "application/json" })
        },
        ...(body && { body }),
      });
      const payload = (await response.json()) as CompetitionEntryResponse | { error?: { message?: string } };
      if (!response.ok) {
        setCompetitionAction((payload as { error?: { message?: string } }).error?.message || "The entry could not be updated.");
        return;
      }
      const result = payload as CompetitionEntryResponse;
      selectCompetition(competitionId);
      setCompetitionDetail(result.competition);
      setDemoAccount(result.account);
      if (method === "POST") requestBattleWriteAccess(user?.telegramId);
      await loadCompetitions();
    } catch {
      setCompetitionAction("The competition service could not be reached.");
    } finally {
      setCompetitionSubmitting(false);
    }
  }

  async function submitCompetitionPick(direction: Direction, roundId: string) {
    if (!authorization || !selectedCompetitionId || competitionSubmitting) return;
    setCompetitionSubmitting(true);
    setCompetitionAction(undefined);
    try {
      const response = await fetch(`${apiBaseUrl}/competitions/${selectedCompetitionId}/picks`, {
        method: "POST",
        headers: { Authorization: authorization, "Content-Type": "application/json" },
        body: JSON.stringify({ roundId, direction }),
      });
      const payload = (await response.json()) as CompetitionPickView | { error?: { message?: string } };
      if (!response.ok) {
        setCompetitionAction((payload as { error?: { message?: string } }).error?.message || "The pick could not be submitted.");
        return;
      }
      triggerHaptic("locked");
      await loadCompetitionDetail(selectedCompetitionId);
    } catch {
      setCompetitionAction("The pick could not be sent before the window closed.");
    } finally {
      setCompetitionSubmitting(false);
    }
  }

  function shareCompetition(competition: CompetitionPoolSummary) {
    const directUrl = botUsername
      ? `https://t.me/${botUsername}?startapp=pool_${competition.id}`
      : `${window.location.origin}?pool=pool_${competition.id}`;
    const shareUrl = `https://t.me/share/url?url=${encodeURIComponent(directUrl)}&text=${encodeURIComponent(`Join my ${competition.durationMinutes}-minute PriceOn battle`)}`;
    if (window.Telegram?.WebApp?.openTelegramLink) {
      window.Telegram.WebApp.openTelegramLink(shareUrl);
    } else {
      window.open(shareUrl, "_blank", "noopener,noreferrer");
    }
  }

  function requestBattleWriteAccess(telegramId: string | undefined) {
    const telegram = window.Telegram?.WebApp;
    if (!telegramId || !telegram?.requestWriteAccess ||
      telegram.initDataUnsafe?.user?.allows_write_to_pm === true) return;
    const key = `priceon:battle-write-access:${telegramId}`;
    try {
      if (window.localStorage.getItem(key)) return;
      window.localStorage.setItem(key, "requested");
    } catch {
      // Telegram still tracks the permission when browser storage is unavailable.
    }
    telegram.requestWriteAccess(() => undefined);
  }

  function dismissBattleAlert() {
    if (battleAlert) markBattleAlertHandled(battleAlert.id);
    setBattleAlert(undefined);
  }

  function openBattleAlert() {
    if (!battleAlert) return;
    const competitionId = battleAlert.id;
    markBattleAlertHandled(competitionId);
    setBattleAlert(undefined);
    selectCompetition(competitionId);
    setActiveTab("battle");
  }

  // Compute nice Y-axis price levels
  return (
    <main className={`app-shell ${showModeSwitch ? "has-mode-switch" : ""}`}>
      <header className="app-header">
        <div className="app-header-main">
          <button className="wordmark" type="button" onClick={() => setActiveTab("play")}>
            <span className="brand-mark"><Zap size={17} fill="currentColor" /></span>
            <span>PriceOn</span>
          </button>
          <div className="header-actions">
            <div className={`connection ${connection}`}>
              <span className="connection-dot" />
              <span>{connectionLabel(connection)}</span>
            </div>
            {gameMode === "live" && activeTab !== "profile" ? (
              <button
                aria-label="Deposit SOL"
                className="deposit-button"
                onClick={() => setActiveTab("profile")}
                title="Deposit SOL"
                type="button"
              >
                <Wallet size={15} strokeWidth={2.4} />
                <span>Deposit</span>
              </button>
            ) : null}
            <button
              className="avatar-button"
              type="button"
              aria-label="Open profile"
              title="Open profile"
              onClick={() => setActiveTab("profile")}
            >
              {initials(displayName)}
            </button>
          </div>
        </div>
        {showModeSwitch ? (
          <div className="app-mode-row">
            <ModeSwitch modes={gameModes} mode={gameMode} onChange={selectGameMode} />
          </div>
        ) : null}
      </header>

      <section className="view" aria-live="polite">
        {activeTab === "play" ? (
          <PlayView
            mode={gameMode}
            gameModes={gameModes}
            userId={user?.id}
            shownResultKeysRef={shownResultKeysRef}
            activePrediction={gameMode === "demo" && !currentDemoTrade ? currentPrediction : undefined}
            activeDemoTrade={currentDemoTrade}
            activeLiveTrade={currentLiveTrade}
            demoAccount={demoAccount}
            demoTrade={upcomingDemoTrade}
            liveTrade={upcomingLiveTrade}
            connection={connection}
            direction={activeStakeDirection}
            now={now}
            onDirection={submitPrediction}
            onReconnect={() => {
              void loadLatestPrice();
              priceReconnectRef.current();
              void refreshRoundState();
            }}
            price={price}
            priceSeries={priceSeries}
            predictionMessage={predictionMessage}
            recentPrediction={previousPrediction}
            recentDemoTrade={previousDemoTrade}
            recentLiveTrade={previousLiveTrade}
            round={activeRound}
            previousRound={previousRound}
            roundMessage={roundMessage}
            secondsRemaining={secondsRemaining}
            submitting={submitting}
            competitionBlocked={gameMode === "demo" && Boolean(blockingCompetition)}
            onOpenBattle={() => {
              if (!battlesEnabled) return;
              if (blockingCompetition) selectCompetition(blockingCompetition.id);
              setActiveTab("battle");
            }}
            upcomingRound={upcomingRound}
            stakePresets={stakePresets}
            stakeSol={stakeSol}
            onStake={setStakeSol}
          />
        ) : null}
        {battlesEnabled && activeTab === "battle" ? (
          <BattleView
            account={demoAccount}
            actionMessage={competitionAction}
            connection={connection}
            dashboard={competitionDashboard}
            detail={competitionDetail}
            gameMode={gameMode}
            now={now}
            onClose={closeBattleDetail}
            onJoin={(id) => void updateCompetitionEntry("POST", id)}
            onLeave={(id) => void updateCompetitionEntry("DELETE", id)}
            onPick={(direction, roundId) => void submitCompetitionPick(direction, roundId)}
            onReconnect={() => {
              void loadLatestPrice();
              priceReconnectRef.current();
              void refreshRoundState();
            }}
            onRetry={() => void loadCompetitions()}
            onSelect={selectCompetition}
            onShare={shareCompetition}
            price={price}
            priceSeries={priceSeries}
            round={activeRound}
            roundMessage={roundMessage}
            secondsRemaining={secondsRemaining}
            selectedId={selectedCompetitionId}
            status={competitionStatus}
            submitting={competitionSubmitting}
          />
        ) : null}
        {activeTab === "stats" ? (
          <StatsView
            mode={gameMode}
            liveMode={gameModes?.live}
            account={demoAccount}
            cursor={gameMode === "live" ? liveHistoryCursor : historyCursor}
            historyStatus={gameMode === "live" ? liveHistoryStatus : historyStatus}
            onLoadMore={() => gameMode === "live"
              ? liveHistoryCursor && void loadLiveHistory(liveHistoryCursor)
              : historyCursor && void loadHistory(historyCursor)}
            onRetryAccount={() => void loadDemoAccount()}
            onRetryHistory={() => gameMode === "live" ? void loadLiveHistory() : void loadHistory()}
            records={gameMode === "live" ? liveHistory : history}
            status={demoStatus}
          />
        ) : null}
        {activeTab === "profile" ? (
          <ProfileView
            name={displayName}
            onRetry={() => { void loadDemoAccount(); void loadWallet(); }}
            user={user}
            demoAccount={demoAccount}
            demoStatus={demoStatus}
            wallet={wallet}
            walletDeposits={walletDeposits}
            walletWithdrawals={walletWithdrawals}
            walletStatus={walletStatus}
            onCancelWithdrawal={cancelWithdrawal}
            onSubmitWithdrawal={submitWithdrawal}
            onReset={resetDemo}
            onReplayTutorial={() => setTutorialOpen(true)}
            referral={referral}
            referralCode={referralCode}
            referralMessage={referralMessage}
            referralSubmitting={referralSubmitting}
            onReferralCodeChange={setReferralCode}
            onActivateReferral={() => void activateReferralCode()}
            onRefreshReferral={() => void loadReferral()}
            gameMode={gameMode}
            gameModes={gameModes}
            onGameModeChange={selectGameMode}
          />
        ) : null}
      </section>

      <BottomNavigation activeTab={activeTab} battlesEnabled={battlesEnabled} onChange={setActiveTab} />
      {battlesEnabled && battleAlert ? (
        <BattleStartModal
          competition={battleAlert}
          onDismiss={dismissBattleAlert}
          onOpen={openBattleAlert}
        />
      ) : tutorialOpen ? <TutorialModal onComplete={() => void updateOnboarding("completed")} onSkip={() => void updateOnboarding("skipped")} /> : null}
    </main>
  );
}

type TerminalResultStatus = "won" | "lost" | "void";
type ChartResult = { key: string; status: TerminalResultStatus };

interface PlayViewProps {
  mode: GameMode;
  gameModes?: GameModesResponse;
  activePrediction?: PredictionView;
  activeDemoTrade?: DemoTradeView;
  activeLiveTrade?: LiveTradeView;
  demoAccount?: DemoAccountView;
  demoTrade?: DemoTradeView;
  liveTrade?: LiveTradeView;
  connection: ConnectionState;
  competitionBlocked: boolean;
  direction?: Direction;
  now: number;
  onDirection(direction: Direction): void;
  onOpenBattle(): void;
  onReconnect(): void;
  predictionMessage?: string;
  recentPrediction?: PredictionView;
  recentDemoTrade?: DemoTradeView;
  recentLiveTrade?: LiveTradeView;
  price?: number;
  priceSeries: PricePoint[];
  round?: DisplayRound;
  result?: ChartResult;
  previousRound?: DisplayRound;
  roundMessage: string;
  secondsRemaining: number;
  submitting: boolean;
  upcomingRound?: RoundView | null;
  stakePresets: string[];
  stakeSol: string;
  onStake(value: string): void;
  userId?: string;
  shownResultKeysRef: React.MutableRefObject<Record<string, string>>;
}

function PlayView(props: PlayViewProps) {
  const decisionPhase = isDecisionPhase(props.now, props.round, props.upcomingRound);
  const latestDecisionPropsRef = useRef<PlayViewProps | undefined>(undefined);
  const wasDecisionPhaseRef = useRef(false);
  const holdRoundRef = useRef<DisplayRound | undefined>(undefined);
  const holdPriceRef = useRef<number | undefined>(undefined);
  const exitTimerRef = useRef<number | undefined>(undefined);
  const resultTimerRef = useRef<number | undefined>(undefined);
  const { shownResultKeysRef } = props;
  const [exitingDecisionProps, setExitingDecisionProps] = useState<PlayViewProps>();
  const [result, setResult] = useState<ChartResult>();
  if (props.round) holdRoundRef.current = props.round;
  if (props.price !== undefined) holdPriceRef.current = props.price;
  
  // Determine if we should show a result for the completed round
  const activeTrade = props.mode === "live" ? props.activeLiveTrade : props.activeDemoTrade;
  const recentTrade = props.mode === "live" ? props.recentLiveTrade : props.recentDemoTrade;
  
  // Get terminal status from completed trades
  const getTerminalStatus = (trade: DemoTradeView | LiveTradeView | undefined): TerminalResultStatus | undefined => {
    if (!trade) return undefined;
    const status = trade.status;
    return status === "won" || status === "lost" || status === "void" ? status : undefined;
  };
  
  // Build result key: prioritize recent trade, then check active if round ended
  let resultStatus: TerminalResultStatus | undefined = undefined;
  let resultRoundId: string | undefined = undefined;
  
  // First check recent (previous) trade - this takes priority
  if (recentTrade) {
    resultStatus = getTerminalStatus(recentTrade);
    if (resultStatus) resultRoundId = recentTrade.roundId;
  }
  
  // If no recent result and current round has ended, check active trade
  if (!resultStatus && props.round && props.now >= props.round.endsAt && activeTrade) {
    resultStatus = getTerminalStatus(activeTrade);
    if (resultStatus) resultRoundId = activeTrade.roundId;
  }
  
  // Fallback to prediction outcome
  if (!resultStatus && props.recentPrediction) {
    const outcome = props.recentPrediction.outcome;
    if (outcome === "won" || outcome === "lost" || outcome === "void") {
      resultStatus = outcome;
      resultRoundId = props.recentPrediction.roundId;
    }
  }
  
  const resultKey = resultStatus && resultRoundId ? `${props.mode}:${resultRoundId}:${resultStatus}` : "";
  const viewProps = { ...props, result };

  if (decisionPhase) {
    latestDecisionPropsRef.current = viewProps;
  }

  useEffect(() => {
    if (!resultKey || !props.userId) return;

    const storageKey = getResultOverlayStorageKey(props.userId, props.mode);
    let lastShownResultKey = shownResultKeysRef.current[storageKey];
    if (lastShownResultKey === undefined) {
      try {
        lastShownResultKey = window.localStorage.getItem(storageKey) ?? "";
      } catch {
        lastShownResultKey = "";
      }
      shownResultKeysRef.current[storageKey] = lastShownResultKey;
    }
    if (!shouldShowResultOverlay(resultKey, lastShownResultKey)) return;

    shownResultKeysRef.current[storageKey] = resultKey;
    try {
      window.localStorage.setItem(storageKey, resultKey);
    } catch {
      // Memory tracking still prevents duplicate overlays during this app session.
    }

    if (resultTimerRef.current !== undefined) {
      window.clearTimeout(resultTimerRef.current);
    }

    setResult({ key: resultKey, status: resultStatus! });
    resultTimerRef.current = window.setTimeout(() => {
      setResult(undefined);
      resultTimerRef.current = undefined;
    }, 1_800);
  }, [props.mode, props.userId, resultKey, resultStatus, shownResultKeysRef]);
  useLayoutEffect(() => {
    if (decisionPhase) {
      wasDecisionPhaseRef.current = true;
      if (exitTimerRef.current !== undefined) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = undefined;
        setExitingDecisionProps(undefined);
      }
      return;
    }
    if (!wasDecisionPhaseRef.current) return;
    wasDecisionPhaseRef.current = false;
    const snapshot = latestDecisionPropsRef.current;
    if (!snapshot) return;

    setExitingDecisionProps(snapshot);
    exitTimerRef.current = window.setTimeout(() => {
      setExitingDecisionProps(undefined);
      exitTimerRef.current = undefined;
    }, decisionPhaseExitMs);
  }, [decisionPhase]);

  useEffect(() => () => {
    if (exitTimerRef.current !== undefined) window.clearTimeout(exitTimerRef.current);
    if (resultTimerRef.current !== undefined) window.clearTimeout(resultTimerRef.current);
  }, []);

  if (decisionPhase) {
    return <DecisionPhaseView {...viewProps} />;
  }

  const liveViewProps = {
    ...viewProps,
    round: props.round ?? holdRoundRef.current ?? props.previousRound,
    price: props.price ?? holdPriceRef.current,
  };
  const liveView = <LivePhaseView {...liveViewProps} />;
  const exitProps = exitingDecisionProps ?? (wasDecisionPhaseRef.current ? latestDecisionPropsRef.current : undefined);
  return (
    <div className={`play-transition-stack ${exitingDecisionProps ? "is-exiting-decision" : ""}`}>
      <div className="play-transition-live">{liveView}</div>
      {exitProps ? (
        <div aria-hidden="true" className="play-transition-exit" inert>
          <DecisionPhaseView {...exitProps} />
        </div>
      ) : null}
    </div>
  );
}

function LivePhaseView(props: PlayViewProps) {
  const chartRound = resolvePlayChartRound(props.round, props.previousRound);
  const liveRound = props.round && props.now < props.round.endsAt ? props.round : undefined;
  const price = props.price ?? liveRound?.startPrice ?? chartRound?.closePrice ?? chartRound?.startPrice;
  const isLive = props.mode === "live";
  const activeTrade = isLive ? props.activeLiveTrade : props.activeDemoTrade;
  const locked = Boolean(props.direction);
  const priceAvailable = isPriceAvailable(props.connection);
  const predictionOpen = isPredictionWindowOpen(props.now, props.upcomingRound);
  const roundPhase = getRoundPhase(props.now, liveRound, props.upcomingRound);
  const roundProgress = liveRound ? getRoundProgressPercent(props.now, liveRound) : 0;
  const liveQuote = isLive ? quoteLiveTrade(props.stakeSol) : undefined;
  const demoQuote = isLive ? undefined : quoteDemoTrade(props.stakeSol);
  const quote = liveQuote ?? demoQuote;
  const stakeValid = isStakeAllowed(props.stakeSol, isLive ? {
    mode: "live",
    liveEnabled: props.gameModes?.live.enabled,
    liveAvailableLamports: props.gameModes?.live.balance.availableLamports,
    liveMinimumStakeLamports: props.gameModes?.live.limits.minimumStakeLamports,
    liveMaximumStakeLamports: props.gameModes?.live.limits.maximumStakeLamports,
  } : {
    mode: "demo",
    demoAvailableBalance: props.demoAccount?.availableBalance,
  });
  const upcomingStartsAt = props.upcomingRound ? Date.parse(props.upcomingRound.startsAt) : NaN;
  const startsIn = Number.isFinite(upcomingStartsAt)
    ? Math.max(0, Math.ceil((upcomingStartsAt - props.now) / 1000))
    : undefined;
  const opensAt = props.upcomingRound
    ? Date.parse(props.upcomingRound.predictionOpensAt)
    : liveRound?.endsAt;
  const opensIn = opensAt === undefined ? 0 : Math.max(0, Math.ceil((opensAt - props.now) / 1000));
  const pickClosesAt = props.upcomingRound ? Date.parse(props.upcomingRound.predictionClosesAt) : liveRound?.endsAt;
  const phaseSecondsRemaining = liveRound
    ? roundPhase === "pick-open" && pickClosesAt !== undefined
      ? Math.max(0, Math.ceil((pickClosesAt - props.now) / 1000))
      : getSecondsRemaining(props.now, liveRound)
    : startsIn ?? 0;
  const countdownLabel = !liveRound
    ? "Starts in"
    : roundPhase === "pick-open"
      ? "Next pick closes"
      : roundPhase === "closing" ? "Closing" : "Ends in";
  const liveDirection = activeTrade?.direction ?? props.activePrediction?.direction;
  const liveTradeStatus = liveRound && liveDirection && price !== undefined
    ? getLiveTradeStatus(liveDirection, liveRound.startPrice, price)
    : undefined;
  const waitingCopy = liveRound
    ? props.upcomingRound ? `Decision opens in ${opensIn}s` : "Preparing next decision"
    : startsIn !== undefined
      ? `Starts in ${startsIn}s`
      : props.roundMessage || "Preparing the next round...";

  return (
    <div className="play-view">
      <section className={`round-bar ${liveRound ? roundPhase : ""}`} aria-label={liveRound ? "Active round" : "Next round"}>
        <div className="round-identity">
          <div className="asset-icon">SOL</div>
          <div>
            <div className="eyebrow">SOL / USDC</div>
            <div className="round-number">{liveRound ? `Round ${formatTime(liveRound.startsAt)}` : "Next round"}</div>
          </div>
        </div>
        <div className="round-times">
          <span><Clock3 size={14} /> {liveRound ? `${formatTime(liveRound.startsAt)} - ${formatTime(liveRound.endsAt)}` : `Starts ${Number.isFinite(upcomingStartsAt) ? formatTime(upcomingStartsAt) : "--:--"}`}</span>
          <small>45-second market round</small>
        </div>
        <div className={`countdown ${liveRound && roundPhase === "closing" ? "closing" : ""}`}>
          <span>{countdownLabel}</span>
          <strong>{liveRound || startsIn !== undefined ? `00:${String(Math.min(phaseSecondsRemaining, 99)).padStart(2, "0")}` : "--"}</strong>
        </div>
        {liveRound ? (
          <div className="round-progress" aria-label={`${Math.round(roundProgress)}% of the current round elapsed`}>
            <span style={{ width: `${roundProgress}%` }} />
          </div>
        ) : null}
      </section>

      {props.connection !== "online" ? (
        <ConnectionNotice connection={props.connection} onReconnect={props.onReconnect} />
      ) : null}

      <LiveMarketPanel
        priceHeaderStatus={(
          <CallStatusIcon
            available={priceAvailable && !props.competitionBlocked && (!isLive || Boolean(props.gameModes?.live.enabled)) && stakeValid}
            locked={locked}
            opensIn={opensIn}
            predictionOpen={predictionOpen}
            stakeLabel={`${formatSolAmount(quote?.stakeSol)} ${isLive ? "SOL" : "Demo SOL"}`}
            totalLabel={`${formatSolAmount(quote?.totalCostSol)} ${isLive ? "SOL" : "Demo SOL"}`}
          />
        )}
        now={props.now}
        points={props.priceSeries}
        price={price}
        priceHeaderBalance={<ModeBalance account={props.demoAccount} live={props.gameModes?.live} mode={props.mode} />}
        primaryDecision={props.competitionBlocked ? (
          <div className="competition-play-block">
            <Swords size={22} />
            <div><strong>Battle in progress</strong><span>Normal 2x trades resume when your competition ends.</span></div>
            <button className="load-more" onClick={props.onOpenBattle} type="button">Open battle</button>
          </div>
        ) : undefined}
        chartOverlay={(
          <div className="chart-decision-overlay" role="status">
            <span>{waitingCopy}</span>
          </div>
        )}
        direction={liveDirection}
        lineChart={Boolean(activeTrade || props.direction)}
        round={chartRound}
      >
        {liveTradeStatus && liveDirection ? (
          <div className={`live-trade-banner ${liveTradeStatus}`}>
            {liveDirection === "up" ? <ArrowUp size={19} /> : <ArrowDown size={19} />}
            <div>
              <span>YOUR {liveDirection.toUpperCase()} TRADE</span>
              <strong>{liveTradeStatus === "neutral" ? "LIVE" : liveTradeStatus.toUpperCase()}</strong>
            </div>
            {activeTrade ? <small>{formatSolAmount(activeTrade.stakeSol)} {isLive ? "SOL" : "Demo SOL"}</small> : null}
          </div>
        ) : null}
         {!activeTrade && !props.activePrediction && !props.competitionBlocked ? (
          <section className="inactive-round-controls">
            <section className="prediction-zone primary-prediction-zone">
              <div className="prediction-buttons">
                <button className="prediction-button up" disabled type="button"><ArrowUp size={24} strokeWidth={3} /><span><strong>PRICE UP</strong><small>Closes above its start</small></span></button>
                <button className="prediction-button down" disabled type="button"><ArrowDown size={24} strokeWidth={3} /><span><strong>PRICE DOWN</strong><small>Closes below its start</small></span></button>
              </div>
            </section>
            <section className="prediction-zone supporting-prediction-zone">
              <div className="stake-picker" aria-label={`${isLive ? "Live" : "Demo"} stake amount`}>
                <span>Stake for next round</span>
                <div className="stake-controls">
                  <div className="stake-presets">
                    {props.stakePresets.map((amount) => (
                      <button disabled key={amount} type="button">{amount}</button>
                    ))}
                  </div>
                  <input aria-label={`Custom stake in ${isLive ? "SOL" : "Demo SOL"}`} disabled inputMode="decimal" readOnly value={props.stakeSol} />
                </div>
                <div className="trade-cost-row">
                  <span>Fee (1%) <strong>{formatSolAmount(quote?.feeSol)} {isLive ? "SOL" : "Demo SOL"}</strong></span>
                  <span>Total <strong>{formatSolAmount(quote?.totalCostSol)} {isLive ? "SOL" : "Demo SOL"}</strong></span>
                </div>
              </div>
            </section>
          </section>
        ) : null}

      </LiveMarketPanel>
    </div>
  );
}

function ResultChartOverlay({ result }: { result?: ChartResult }) {
  if (!result) return null;
  return (
    <div className={`result-chart-overlay ${result.status}`} key={result.key} role="status" aria-live="polite">
      <strong>{result.status === "void" ? "REFUNDED" : result.status.toUpperCase()}</strong>
    </div>
  );
}

function DecisionPhaseView(props: PlayViewProps) {
  const isLive = props.mode === "live";
  const selectedChartPriceRef = useRef<{ roundId: string; price: number } | undefined>(undefined);
  const settledRound = props.previousRound ?? props.round;
  const predictionOpen = isPredictionWindowOpen(props.now, props.upcomingRound);
  const priceAvailable = isPriceAvailable(props.connection);
  const locked = Boolean(props.direction);
  const liveQuote = isLive ? quoteLiveTrade(props.stakeSol) : undefined;
  const demoQuote = isLive ? undefined : quoteDemoTrade(props.stakeSol);
  const quote = liveQuote ?? demoQuote;
  const stakeValid = isStakeAllowed(props.stakeSol, isLive ? {
    mode: "live",
    liveEnabled: props.gameModes?.live.enabled,
    liveAvailableLamports: props.gameModes?.live.balance.availableLamports,
    liveMinimumStakeLamports: props.gameModes?.live.limits.minimumStakeLamports,
    liveMaximumStakeLamports: props.gameModes?.live.limits.maximumStakeLamports,
  } : {
    mode: "demo",
    demoAvailableBalance: props.demoAccount?.availableBalance,
  });
  const closesAt = props.upcomingRound ? Date.parse(props.upcomingRound.predictionClosesAt) : props.now;
  const closesIn = Math.max(0, Math.ceil((closesAt - props.now) / 1000));
  const upcomingStartsAt = props.upcomingRound ? Date.parse(props.upcomingRound.startsAt) : NaN;
  const startsIn = Number.isFinite(upcomingStartsAt)
    ? Math.max(0, Math.ceil((upcomingStartsAt - props.now) / 1000))
    : 0;
  const targetLabel = props.upcomingRound
    ? `Round ${formatTime(Date.parse(props.upcomingRound.startsAt))}`
    : "next round";

  if (!props.direction) {
    selectedChartPriceRef.current = undefined;
  } else if (
    props.upcomingRound && props.price !== undefined
    && selectedChartPriceRef.current?.roundId !== props.upcomingRound.id
  ) {
    selectedChartPriceRef.current = { roundId: props.upcomingRound.id, price: props.price };
  }

  const freshChartRound = toScheduledDisplayRound(
    props.upcomingRound,
    props.direction ? (selectedChartPriceRef.current?.price ?? props.price) : props.price,
  );
  const contextRound = settledRound ?? freshChartRound;
  const displayPrice = settledRound
    ? settledRound.closePrice ?? settledRound.startPrice
    : props.price;
  const selectedChartPrice = selectedChartPriceRef.current?.roundId === props.upcomingRound?.id
    ? selectedChartPriceRef.current?.price ?? props.price
    : props.price;
  return (
    <div className="play-view decision-phase">
      <section className="round-bar pick-open" aria-label="Decision phase">
        <div className="round-identity">
          <div className="asset-icon">SOL</div>
          <div>
            <div className="eyebrow">DECISION PHASE</div>
            <div className="round-number">Choose for {targetLabel}</div>
          </div>
        </div>
        <div className="round-times">
          <span><Clock3 size={14} /> Starts {props.upcomingRound ? formatTime(Date.parse(props.upcomingRound.startsAt)) : "--:--"}</span>
          <small>15 seconds to decide</small>
        </div>
        <div className="countdown">
          <span>Decision closes</span>
          <strong>00:{String(closesIn).padStart(2, "0")}</strong>
        </div>
      </section>

      {props.connection !== "online" ? <ConnectionNotice connection={props.connection} onReconnect={props.onReconnect} /> : null}

      <LiveMarketPanel
        priceHeaderStatus={(
          <CallStatusIcon
            available={priceAvailable && !props.competitionBlocked && (!isLive || Boolean(props.gameModes?.live.enabled)) && stakeValid}
            locked={locked}
            opensIn={0}
            predictionOpen={predictionOpen}
            stakeLabel={`${formatSolAmount(quote?.stakeSol)} ${isLive ? "SOL" : "Demo SOL"}`}
            totalLabel={`${formatSolAmount(quote?.totalCostSol)} ${isLive ? "SOL" : "Demo SOL"}`}
          />
        )}
        now={props.now}
        points={props.priceSeries}
        price={displayPrice}
        chartPrice={freshChartRound ? props.now < upcomingStartsAt ? selectedChartPrice : props.price : undefined}
        chartRound={freshChartRound}
        priceHeaderBalance={<ModeBalance account={props.demoAccount} live={props.gameModes?.live} mode={props.mode} />}
        priceLabel="ROUND CLOSE"
        direction={props.direction}
        lineChart={Boolean(props.direction)}
        chartOverlay={props.direction ? (
          <>
            <ResultChartOverlay result={props.result} />
            <div className="chart-decision-overlay chart-start-countdown" role="status">
              <span>STARTS IN</span>
              <strong>{startsIn}s</strong>
            </div>
          </>
        ) : <ResultChartOverlay result={props.result} />}
        round={freshChartRound ?? contextRound}
      >
        {props.competitionBlocked ? (
          <div className="competition-play-block">
            <Swords size={22} />
            <div><strong>Battle in progress</strong><span>Normal 2x trades resume when your competition ends.</span></div>
            <button className="load-more" onClick={props.onOpenBattle} type="button">Open battle</button>
          </div>
        ) : (
          <section className="prediction-zone primary-prediction-zone decision-open">
            <div className="prediction-buttons">
              <button className={`prediction-button up ${props.direction === "up" ? "selected" : ""}`} disabled={locked || props.submitting || !priceAvailable || !predictionOpen || !stakeValid} onClick={() => props.onDirection("up")} type="button"><ArrowUp size={24} strokeWidth={3} /><span><strong>PRICE UP</strong><small>{props.direction === "up" ? "SELECTED" : "Closes above its start"}</small></span></button>
              <button className={`prediction-button down ${props.direction === "down" ? "selected" : ""}`} disabled={locked || props.submitting || !priceAvailable || !predictionOpen || !stakeValid} onClick={() => { console.log("CLICKED DOWN"); props.onDirection("down"); }} type="button"><ArrowDown size={24} strokeWidth={3} /><span><strong>PRICE DOWN</strong><small>{props.direction === "down" ? "SELECTED" : "Closes below its start"}</small></span></button>
            </div>
            {!locked ? (
              <div className="prediction-status">
                {props.submitting ? <><RefreshCw className="spin" size={15} /> Sending prediction...</>
                  : !priceAvailable ? <><WifiOff size={15} /> {connectionMessage(props.connection)}</>
                    : isLive && !props.gameModes?.live.enabled ? <><ShieldAlert size={15} /> {liveUnavailableMessage(props.gameModes?.live.reason)}</>
                      : !stakeValid ? <><ShieldAlert size={15} /> Enter an allowed stake with enough {isLive ? "SOL" : "Demo SOL"} for the 1% fee.</>
                        : props.predictionMessage ? <><ShieldAlert size={15} /> {props.predictionMessage}</>
                          : !predictionOpen ? <><Clock3 size={15} /> Wait for the next decision window.</>
                            : null}
              </div>
            ) : null}
          </section>
        )}

        {!props.competitionBlocked ? (
          <section className={`prediction-zone supporting-prediction-zone ${locked ? "is-locked" : ""}`}>
            <div className="stake-picker" aria-label={`${isLive ? "Live" : "Demo"} stake amount`}>
              <span>Stake for {targetLabel}</span>
              <div className="stake-controls">
                <div className="stake-presets">
                  {props.stakePresets.map((amount) => {
                    const allowed = isStakeAllowed(amount, isLive ? {
                      mode: "live",
                      liveEnabled: props.gameModes?.live.enabled,
                      liveAvailableLamports: props.gameModes?.live.balance.availableLamports,
                      liveMinimumStakeLamports: props.gameModes?.live.limits.minimumStakeLamports,
                      liveMaximumStakeLamports: props.gameModes?.live.limits.maximumStakeLamports,
                    } : {
                      mode: "demo",
                      demoAvailableBalance: props.demoAccount?.availableBalance,
                    });
                    return (
                      <button className={props.stakeSol === amount ? "active" : ""} disabled={locked || !allowed} key={amount} onClick={() => props.onStake(amount)} type="button">{amount}</button>
                    );
                  })}
                </div>
                <input disabled={locked} inputMode="decimal" min={isLive ? props.gameModes?.live.limits.minimumStakeSol : "0.01"} max={isLive ? props.gameModes?.live.limits.maximumStakeSol : undefined} step={isLive ? "0.001" : "0.01"} value={props.stakeSol} onChange={(event) => props.onStake(event.target.value)} aria-label={`Custom stake in ${isLive ? "SOL" : "Demo SOL"}`} />
              </div>
              <div className="trade-cost-row">
                <span>Fee (1%) <strong>{formatSolAmount(quote?.feeSol)} {isLive ? "SOL" : "Demo SOL"}</strong></span>
                <span>Total <strong>{formatSolAmount(quote?.totalCostSol)} {isLive ? "SOL" : "Demo SOL"}</strong></span>
              </div>
            </div>
          </section>
        ) : null}
      </LiveMarketPanel>
    </div>
  );
}

function ConnectionNotice({ connection, onReconnect }: { connection: ConnectionState; onReconnect(): void }) {
  const actionable = connection === "device-offline" || connection === "reconnecting";
  return (
    <div className={`price-connection-notice ${connection}`} role="status">
      <WifiOff size={17} />
      <span>{connectionMessage(connection)}</span>
      {actionable ? <button onClick={onReconnect} type="button"><RefreshCw size={14} /> Reconnect</button> : null}
    </div>
  );
}

function ModeSwitch({ mode, modes, onChange }: {
  mode: GameMode;
  modes?: GameModesResponse;
  onChange(mode: GameMode): void;
}) {
  if (!modes?.live.invited) return null;
  return (
    <div className="game-mode-switch" role="group" aria-label="Trading mode">
      <button
        aria-pressed={mode === "live"}
        className={mode === "live" ? "active live" : "live"}
        disabled={modes.live.reason === "disabled"}
        onClick={() => onChange("live")}
        type="button"
      >{modes.live.network === "devnet" ? "Devnet" : "Live"}</button>
      <button aria-pressed={mode === "demo"} className={mode === "demo" ? "active" : ""} onClick={() => onChange("demo")} type="button">Demo</button>
    </div>
  );
}

function ModeBalance({ account, live, mode }: {
  account?: DemoAccountView;
  live?: GameModesResponse["live"];
  mode: GameMode;
}) {
  const isLive = mode === "live";
  const label = isLive ? (live?.network === "devnet" ? "DEVNET SOL" : "SOL") : "DEMO SOL";
  const balance = formatPrimarySol(isLive ? live?.balance.availableSol : account?.availableBalance);

  return (
    <div aria-label={`${label} balance ${balance}`} className={`price-balance ${isLive ? "live" : "demo"}`}>
      <span>{label}</span>
      <strong>{balance}</strong>
    </div>
  );
}

function connectionLabel(connection: ConnectionState): string {
  if (connection === "online") return "Connected";
  if (connection === "connecting") return "Connecting";
  if (connection === "reconnecting") return "Reconnecting";
  if (connection === "server-unavailable") return "Unavailable";
  return "Offline";
}

function connectionMessage(connection: ConnectionState): string {
  if (connection === "connecting") return "Connecting to the live price service...";
  if (connection === "reconnecting") return "Live price connection lost. Reconnecting automatically...";
  if (connection === "server-unavailable") return "The server is waiting for a fresh SOL price. Your prediction is paused.";
  if (connection === "device-offline") return "Your device is offline. Reconnect to continue live updates.";
  return "Live price updates are active.";
}

function CallStatusIcon({
  available,
  locked,
  opensIn,
  predictionOpen,
  stakeLabel,
  totalLabel,
}: {
  available: boolean;
  locked: boolean;
  opensIn: number;
  predictionOpen: boolean;
  stakeLabel: string;
  totalLabel: string;
}) {
  const state = locked
    ? `Call locked · ${stakeLabel} · Total ${totalLabel}`
    : !available
      ? "Call unavailable"
      : predictionOpen
        ? `Make your next call · ${stakeLabel} · Total ${totalLabel}`
        : opensIn > 0 ? `Next call opens in ${opensIn}s · ${stakeLabel}` : "Preparing the next call";
  const status = locked ? "locked" : !available ? "unavailable" : predictionOpen ? "ready" : "waiting";

  return (
    <span aria-label={state} className={`call-status-icon ${status}`} role="img" title={state}>
      {locked ? <Target size={15} /> : !available ? <ShieldAlert size={15} /> : predictionOpen ? <Sparkles size={15} /> : <Clock3 size={15} />}
    </span>
  );
}

function LiveMarketPanel({ chartOverlay, chartPrice, chartRound, children, direction, lineChart = false, now, points, price, priceHeaderBalance, priceHeaderStatus, primaryDecision, priceLabel = "LIVE PRICE", round }: {
  children?: ReactNode;
  chartOverlay?: ReactNode;
  chartPrice?: number;
  chartRound?: DisplayRound;
  direction?: Direction;
  lineChart?: boolean;
  now: number;
  points: PricePoint[];
  price?: number;
  priceHeaderBalance?: ReactNode;
  priceHeaderStatus?: ReactNode;
  primaryDecision?: ReactNode;
  priceLabel?: string;
  round?: DisplayRound;
}) {
  const change = round && price !== undefined ? price - round.startPrice : undefined;
  const percentage = change !== undefined && round ? (change / round.startPrice) * 100 : undefined;
  const movement = change === undefined ? "flat" : change > 0 ? "up" : change < 0 ? "down" : "flat";
  const previousPriceRef = useRef(price);
  const previousPrice = previousPriceRef.current;
  const tickDirection = price === undefined || previousPrice === undefined || price === previousPrice
    ? "flat"
    : price > previousPrice ? "up" : "down";

  useEffect(() => {
    if (price !== undefined) previousPriceRef.current = price;
  }, [price]);

  return (
    <section className="market-panel">
      <div className="price-header">
        <div>
          <div className="eyebrow live-label"><Activity size={13} /> {priceLabel}</div>
          <div className="current-price"><span className={`price-value tick-${tickDirection}`} key={`${round?.id ?? "waiting"}:${price ?? "none"}`}>{price === undefined ? "$--" : `$${price.toFixed(2)}`}</span></div>
          <div className={`price-tick ${tickDirection}`}>{price === undefined || previousPrice === undefined || tickDirection === "flat" ? "Awaiting next tick" : `Tick ${formatPriceDelta(price - previousPrice)}`}</div>
        </div>
        {priceHeaderBalance}
        <div className="price-header-actions">
          {priceHeaderStatus}
          {change !== undefined && percentage !== undefined ? (
            <div className={`price-change ${movement}`}>
              {movement === "down" ? <ArrowDown size={16} /> : movement === "up" ? <ArrowUp size={16} /> : <Activity size={16} />}
              <strong>{formatPriceDelta(change)}</strong>
              <span>{percentage >= 0 ? "+" : ""}{percentage.toFixed(2)}%</span>
            </div>
          ) : null}
        </div>
      </div>

      <PriceChart chartOverlay={chartOverlay} direction={direction} lineChart={lineChart} now={now} points={points} price={chartPrice ?? price} round={chartRound ?? round} />

      {round ? (
        <div className="chart-footer">
          <span>Round start <strong>${round.startPrice.toFixed(2)}</strong></span>
          <span className={`direction-copy ${movement}`}>
            {movement === "down" ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
            {movement === "down" ? "Below start" : movement === "up" ? "Above start" : "At start"}
          </span>
        </div>
      ) : null}
      {primaryDecision ? <div className="market-primary-decision">{primaryDecision}</div> : null}
      {children ? <div className="market-decision">{children}</div> : null}
    </section>
  );
}

function ChartFrame({
  chartOverlay,
  children,
  className,
  loadingLabel,
  startLabelTop,
}: {
  chartOverlay?: ReactNode;
  children?: ReactNode;
  className: string;
  loadingLabel?: string;
  startLabelTop?: string;
}) {
  return (
    <div className={className}>
      <div className="chart-plot">
        {loadingLabel ? (
          <div className="chart-loading-overlay">
            <div className="chart-loading-pulse" />
            <span>{loadingLabel}</span>
          </div>
        ) : null}
        {children}
        {startLabelTop !== undefined ? (
          <span className="start-label" style={{ top: startLabelTop }}>START</span>
        ) : null}
        {chartOverlay}
      </div>
      <div className="time-axis"><span>9m ago</span><span>6m ago</span><span>3m ago</span><span>NOW</span></div>
    </div>
  );
}

function PriceChart({ chartOverlay, direction, lineChart, now, points, price, round }: {
  chartOverlay?: ReactNode;
  direction?: Direction;
  lineChart: boolean;
  now: number;
  points: PricePoint[];
  price?: number;
  round?: DisplayRound;
}) {
  const width = 680;
  const height = 210;
  const displayedPriceRef = useRef(price ?? 0);
  const roundIdRef = useRef(round?.id);
  const lastPaintRef = useRef<PaintedPriceChart | undefined>(undefined);
  const domainRef = useRef<(ChartPriceDomainKey & { min: number; max: number }) | undefined>(undefined);
  const [displayedPrice, setDisplayedPrice] = useState(price ?? 0);
  const heldChart = holdPaintedPriceChart({
    price,
    points,
    round,
    now,
    lineChart,
    lastPaint: lastPaintRef.current,
  });
  if (heldChart.paint) lastPaintRef.current = heldChart.paint;
  const paint = heldChart.paint;
  const isLoading = heldChart.showLoading;
  
  useEffect(() => {
    if (price === undefined) return;
    if (roundIdRef.current !== round?.id) {
      roundIdRef.current = round?.id;
      displayedPriceRef.current = price;
      setDisplayedPrice(price);
      return;
    }
    const from = displayedPriceRef.current;
    if (from === price || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      displayedPriceRef.current = price;
      setDisplayedPrice(price);
      return;
    }
    const startedAt = performance.now();
    let animationFrame = 0;
    const animate = (timestamp: number) => {
      const progress = Math.min(1, (timestamp - startedAt) / 220);
      const eased = 1 - (1 - progress) ** 3;
      const next = from + (price - from) * eased;
      displayedPriceRef.current = next;
      setDisplayedPrice(next);
      if (progress < 1) animationFrame = window.requestAnimationFrame(animate);
    };
    animationFrame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [price, round?.id]);

  if (!paint || isLoading) {
    return (
      <ChartFrame
        className="chart chart-loading"
        chartOverlay={chartOverlay}
        loadingLabel="Waiting for price data…"
      />
    );
  }

  const chartPoints = paint.points;
  const chartRound = paint.round;
  const chartPrice = price !== undefined ? displayedPrice : paint.price;

  // Round mode: use round boundaries
  if (chartRound) {
    const historicalPoints = lineChart
      ? [
          { timestamp: chartRound.startsAt, price: chartRound.startPrice },
          ...chartPoints.filter((point) => point.timestamp >= chartRound.startsAt && point.timestamp < chartRound.endsAt),
        ]
      : [
          { timestamp: chartRound.startsAt, price: chartRound.startPrice },
          ...chartPoints,
        ];
    const latestPoint = historicalPoints.at(-1)!;
    const liveTimestamp = Math.max(latestPoint.timestamp, Math.min(now, chartRound.endsAt - 1));

    const displayCandles = generateCandlesticks(historicalPoints, chartPrice, now, chartRound);

    // Keep the scale stable as live prices arrive, but recenter after a start-price or chart-mode change.
    const domainKey = { roundId: chartRound.id, startPrice: chartRound.startPrice, lineChart };
    const previousDomain = canReuseChartPriceDomain(domainRef.current, domainKey) ? domainRef.current : undefined;
    const allPrices = lineChart
      ? [...historicalPoints.map((point) => point.price), chartPrice]
      : [
        chartRound.startPrice,
        chartPrice,
        ...displayCandles.flatMap((c) => [c.high, c.low, c.open, c.close]),
      ];

    const domain = getExpandedPriceDomain(
      allPrices.map((price, i) => ({ timestamp: chartRound.startsAt + (i * 1000), price })),
      chartRound.startPrice,
      previousDomain,
    );
    domainRef.current = { ...domainKey, ...domain };

    const range = Math.max(0.01, domain.max - domain.min);
    const y = (value: number) => height - ((value - domain.min) / range) * height;

    const chartTone = getChartTone(chartRound.startPrice, chartPrice, direction);
    const x = (timestamp: number) => (getRoundTimePercent(timestamp, chartRound) / 100) * width;
    const historyLine = historicalPoints.map((point) => `${x(point.timestamp)},${y(point.price)}`).join(" ");
    const tailPoint = `${x(liveTimestamp)},${y(chartPrice)}`;
    const area = `0,${height} ${historyLine} ${tailPoint} ${x(liveTimestamp)},${height}`;
    const gradientId = `chart-area-${chartRound.id.replace(/[^a-zA-Z0-9_-]/g, "")}`;

    // Layout: 9 candlesticks evenly spaced across the width
    const numCandles = 9;
    const candleWidth = width / numCandles;
    const padding = 8;
    const bodyWidth = candleWidth - padding * 2;

    // Decision phase line: positioned exactly in the middle between 8th and 9th candle
    const decisionPhaseX = (candleWidth * 8) + padding;

    return (
      <ChartFrame
        className={`chart ${chartTone}${isLoading ? " chart-loading" : ""}`}
        chartOverlay={chartOverlay}
        loadingLabel={isLoading ? "Loading chart…" : undefined}
        startLabelTop={`${(y(chartRound.startPrice) / height) * 100}%`}
      >
        <svg aria-label={lineChart ? "SOL price during the current round" : "SOL price candlesticks - last 9 minutes"} role="img" viewBox={`0 0 ${width} ${height}`}>
          <defs>
            {lineChart ? (
              <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.3" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
              </linearGradient>
            ) : (
              <style>{`
                .candle-body { stroke-width: 1; }
                .candle-wick { stroke-width: 1; }
                .candle-up .candle-body { fill: #10b981; stroke: #10b981; }
                .candle-up .candle-wick { stroke: #10b981; }
                .candle-down .candle-body { fill: #ef4444; stroke: #ef4444; }
                .candle-down .candle-wick { stroke: #ef4444; }
                .candle-flat .candle-body { fill: #6b7280; stroke: #6b7280; }
                .candle-flat .candle-wick { stroke: #6b7280; }
              `}</style>
            )}
          </defs>
          {lineChart ? (
            <>
              <polyline fill={`url(#${gradientId})`} points={area} vectorEffect="non-scaling-stroke" />
              <polyline fill="none" points={`${historyLine} ${tailPoint}`} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" vectorEffect="non-scaling-stroke" />
              <line stroke="currentColor" strokeDasharray="4 4" strokeWidth="1.5" vectorEffect="non-scaling-stroke" x1="0" x2={width} y1={y(chartRound.startPrice)} y2={y(chartRound.startPrice)} />
              {/* Circle at the end of trade line */}
              <circle cx={x(liveTimestamp)} cy={y(chartPrice)} r="4" fill="currentColor" stroke="white" strokeWidth="2" />
            </>
          ) : (
            <>
              <g className="grid-lines">
                <line x1="0" x2={width} y1="42" y2="42" stroke="currentColor" strokeWidth="1" opacity="0.1" />
                <line x1="0" x2={width} y1="105" y2="105" stroke="currentColor" strokeWidth="1" opacity="0.1" />
                <line x1="0" x2={width} y1="168" y2="168" stroke="currentColor" strokeWidth="1" opacity="0.1" />
              </g>

              {/* Start price horizontal line */}
              <line className="start-line" x1="0" x2={width} y1={y(chartRound.startPrice)} y2={y(chartRound.startPrice)} stroke="currentColor" strokeWidth="2" opacity="0.5" />

              {/* Candlesticks */}
              <g className="candlesticks">
                {displayCandles.map((candle, i) => {
                  const candleX = padding + (i * candleWidth) + (candleWidth / 2);
                  const openY = y(candle.open);
                  const closeY = y(candle.close);
                  const highY = y(candle.high);
                  const lowY = y(candle.low);

                  const bodyTop = Math.min(openY, closeY);
                  const bodyBottom = Math.max(openY, closeY);
                  const bodyHeight = Math.max(1, bodyBottom - bodyTop);
                  const candleClass = candle.close > candle.open ? "candle-up" : candle.close < candle.open ? "candle-down" : "candle-flat";

                  return (
                    <g key={`candle-${i}`} className={candleClass} opacity={candle.isLive ? 1 : 0.7}>
                      {/* Wick (high-low) */}
                      <line className="candle-wick" x1={candleX} y1={highY} x2={candleX} y2={lowY} />
                      {/* Body (open-close) */}
                      <rect
                        className="candle-body"
                        x={candleX - bodyWidth / 2}
                        y={bodyTop}
                        width={bodyWidth}
                        height={bodyHeight}
                      />
                    </g>
                  );
                })}
              </g>

              {/* Decision phase line */}
              <line
                className="decision-phase-line"
                x1={decisionPhaseX}
                y1="0"
                x2={decisionPhaseX}
                y2={height}
                strokeWidth="2"
                stroke="rgba(255, 193, 7, 0.6)"
                strokeDasharray="4,4"
              />
            </>
          )}
        </svg>
      </ChartFrame>
    );
  }
  
  // Fallback mode: no round, just show candles
  const displayCandles = generateCandlesticks(chartPoints, chartPrice, now, undefined, 10);
  
  if (displayCandles.length === 0) {
    return (
      <ChartFrame
        className="chart chart-loading"
        chartOverlay={chartOverlay}
        loadingLabel="Waiting for price data…"
      />
    );
  }

  const allPrices = displayCandles.flatMap((c) => [c.high, c.low, c.open, c.close]);
  const minPrice = Math.min(...allPrices);
  const maxPrice = Math.max(...allPrices);
  const startPrice = displayCandles[0]?.open ?? chartPrice;
  const range = Math.max(0.01, maxPrice - minPrice);
  const y = (value: number) => height - ((value - minPrice) / range) * height;

  const latestCandle = displayCandles[displayCandles.length - 1];
  const isUp = latestCandle ? latestCandle.close >= displayCandles[0].open : true;

  // Layout: 10 candlesticks evenly spaced across the width
  const numCandles = 10;
  const candleWidth = width / numCandles;
  const padding = 8;
  const bodyWidth = candleWidth - padding * 2;

  return (
    <ChartFrame
      className={`chart ${isUp ? "up" : "down"}`}
      chartOverlay={chartOverlay}
      startLabelTop={`${(y(startPrice) / height) * 100}%`}
    >
      <svg aria-label="SOL price candlesticks - market monitoring" role="img" viewBox={`0 0 ${width} ${height}`}>
        <defs>
          <style>{`
            .candle-body { stroke-width: 1; }
            .candle-wick { stroke-width: 1; }
            .candle-up .candle-body { fill: #10b981; stroke: #10b981; }
            .candle-up .candle-wick { stroke: #10b981; }
            .candle-down .candle-body { fill: #ef4444; stroke: #ef4444; }
            .candle-down .candle-wick { stroke: #ef4444; }
            .candle-flat .candle-body { fill: #6b7280; stroke: #6b7280; }
            .candle-flat .candle-wick { stroke: #6b7280; }
          `}</style>
        </defs>
        <g className="grid-lines">
          <line x1="0" x2={width} y1="42" y2="42" stroke="currentColor" strokeWidth="1" opacity="0.1" />
          <line x1="0" x2={width} y1="105" y2="105" stroke="currentColor" strokeWidth="1" opacity="0.1" />
          <line x1="0" x2={width} y1="168" y2="168" stroke="currentColor" strokeWidth="1" opacity="0.1" />
        </g>

        {/* Start price horizontal line */}
        <line className="start-line" x1="0" x2={width} y1={y(startPrice)} y2={y(startPrice)} stroke="currentColor" strokeWidth="2" opacity="0.5" />

        <g className="candlesticks">
          {displayCandles.map((candle, i) => {
            const candleX = padding + (i * candleWidth) + (candleWidth / 2);
            const openY = y(candle.open);
            const closeY = y(candle.close);
            const highY = y(candle.high);
            const lowY = y(candle.low);

            const bodyTop = Math.min(openY, closeY);
            const bodyBottom = Math.max(openY, closeY);
            const bodyHeight = Math.max(1, bodyBottom - bodyTop);
            const candleClass = candle.close > candle.open ? "candle-up" : candle.close < candle.open ? "candle-down" : "candle-flat";

            return (
              <g key={`candle-${i}`} className={candleClass}>
                {/* Wick (high-low) */}
                <line className="candle-wick" x1={candleX} y1={highY} x2={candleX} y2={lowY} />
                {/* Body (open-close) */}
                <rect
                  className="candle-body"
                  x={candleX - bodyWidth / 2}
                  y={bodyTop}
                  width={bodyWidth}
                  height={bodyHeight}
                />
              </g>
            );
          })}
        </g>
      </svg>
    </ChartFrame>
  );
}
function BattleView({
  account,
  actionMessage,
  connection,
  dashboard,
  detail,
  gameMode,
  now,
  onClose,
  onJoin,
  onLeave,
  onPick,
  onReconnect,
  onRetry,
  onSelect,
  onShare,
  price,
  priceSeries,
  round,
  roundMessage,
  secondsRemaining,
  selectedId,
  status,
  submitting,
}: {
  account?: DemoAccountView;
  actionMessage?: string;
  connection: ConnectionState;
  dashboard?: CompetitionDashboardResponse;
  detail?: CompetitionPoolDetail;
  gameMode: GameMode;
  now: number;
  onClose(): void;
  onJoin(id: string): void;
  onLeave(id: string): void;
  onPick(direction: Direction, roundId: string): void;
  onReconnect(): void;
  onRetry(): void;
  onSelect(id: string): void;
  onShare(competition: CompetitionPoolSummary): void;
  price?: number;
  priceSeries: PricePoint[];
  round?: DisplayRound;
  roundMessage: string;
  secondsRemaining: number;
  selectedId?: string;
  status: DataStatus;
  submitting: boolean;
}) {
  const playableDetail = detail?.myEntry &&
    (detail.myEntry.status === "joined" || detail.myEntry.status === "active") &&
    (detail.status === "open" || detail.status === "active")
    ? detail
    : undefined;

  return (
    <div className="content-view battle-view">
      <ViewHeading eyebrow="PUBLIC POOLS" title="Battles" subtitle="Timed SOL prediction pools with one shared prize." />

      {status === "error" && !dashboard ? (
        <DataState action="Retry" message="Battles are temporarily unavailable." onAction={onRetry} />
      ) : !dashboard ? (
        <DataState loading message="Loading upcoming battles..." />
      ) : (
        <>
          {playableDetail ? (
            <BattleExperience
              account={account}
              actionMessage={actionMessage}
              connection={connection}
              detail={playableDetail}
              now={now}
              onLeave={onLeave}
              onPick={onPick}
              onReconnect={onReconnect}
              onShare={onShare}
              price={price}
              priceSeries={priceSeries}
              round={round}
              roundMessage={roundMessage}
              secondsRemaining={secondsRemaining}
              submitting={submitting}
            />
          ) : null}

          {detail && !playableDetail ? (
            <BattleOverview
              account={account}
              actionMessage={actionMessage}
              detail={detail}
              gameMode={gameMode}
              now={now}
              onClose={onClose}
              onJoin={onJoin}
              onLeave={onLeave}
              onShare={onShare}
              submitting={submitting}
            />
          ) : null}

          <section className="battle-section">
            <div className="section-heading"><h2>Next pools</h2><span>Entry closes 1m before start</span></div>
            <div className="pool-grid">
              {dashboard.upcoming.map((competition) => (
                <PoolCard
                  competition={competition}
                  gameMode={gameMode}
                  key={competition.id}
                  now={now}
                  onJoin={onJoin}
                  onLeave={onLeave}
                  onSelect={onSelect}
                  onShare={onShare}
                  selected={selectedId === competition.id}
                  submitting={submitting}
                />
              ))}
            </div>
          </section>

          {dashboard.recent.length > 0 ? (
            <section className="battle-section">
              <div className="section-heading"><h2>Recent battles</h2><span>{dashboard.recent.length} results</span></div>
              <div className="list-panel compact-battle-list">{dashboard.recent.map((competition) => <button key={competition.id} onClick={() => onSelect(competition.id)} type="button"><Swords size={17} /><span><strong>{competition.durationMinutes} min pool</strong><small>{formatTime(Date.parse(competition.startsAt))} · {competition.participantCount} players</small></span><b>{competition.status === "completed" ? formatDemoSol(competition.prizePoolSol) : "REFUND"}</b></button>)}</div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}

function BattleExperience({
  account,
  actionMessage,
  connection,
  detail,
  now,
  onLeave,
  onPick,
  onReconnect,
  onShare,
  price,
  priceSeries,
  round,
  roundMessage,
  secondsRemaining,
  submitting,
}: {
  account?: DemoAccountView;
  actionMessage?: string;
  connection: ConnectionState;
  detail: CompetitionPoolDetail;
  now: number;
  onLeave(id: string): void;
  onPick(direction: Direction, roundId: string): void;
  onReconnect(): void;
  onShare(competition: CompetitionPoolSummary): void;
  price?: number;
  priceSeries: PricePoint[];
  round?: DisplayRound;
  roundMessage: string;
  secondsRemaining: number;
  submitting: boolean;
}) {
  const timeline = buildBattleTimeline(detail, now);
  const liveRound = timeline.find((item) => item.phase === "live");
  const nextRound = timeline.find((item) => now < item.endsAt);
  const displayRound = liveRound ?? nextRound ?? timeline.at(-1);
  const beforeStart = now < Date.parse(detail.startsAt);
  const countdownSeconds = beforeStart
    ? Math.max(0, Math.ceil((Date.parse(detail.startsAt) - now) / 1000))
    : liveRound
      ? Math.max(0, Math.ceil((liveRound.endsAt - now) / 1000))
      : detail.status === "active"
        ? Math.max(0, Math.ceil((Date.parse(detail.endsAt) - now) / 1000))
        : Math.max(0, Math.ceil(((nextRound?.startsAt ?? Date.parse(detail.endsAt)) - now) / 1000));
  const progress = getBattleProgressPercent(now, detail.startsAt, detail.endsAt);
  const openPickRound = timeline.find((item) => item.phase === "pick-open" && item.acceptingPick);
  const latestSettledRound = [...timeline].reverse().find((item) => (
    item.phase === "won" || item.phase === "lost" || item.phase === "void"
  ));
  const battlePickFeedbackRef = useRef<string | undefined>(undefined);
  const battleResultFeedbackRef = useRef<string | undefined>(undefined);
  const livePickStatus = liveRound?.myDirection && round && price !== undefined
    ? getLiveTradeStatus(liveRound.myDirection, round.startPrice, price)
    : undefined;

  useEffect(() => {
    const key = openPickRound?.key ?? "";
    if (battlePickFeedbackRef.current === undefined) {
      battlePickFeedbackRef.current = key;
      return;
    }
    if (key && key !== battlePickFeedbackRef.current && document.visibilityState === "visible") {
      triggerHaptic("pick-open");
    }
    battlePickFeedbackRef.current = key;
  }, [openPickRound?.key]);

  useEffect(() => {
    const key = latestSettledRound ? `${latestSettledRound.key}:${latestSettledRound.phase}` : "";
    if (battleResultFeedbackRef.current === undefined) {
      battleResultFeedbackRef.current = key;
      return;
    }
    if (key && key !== battleResultFeedbackRef.current && document.visibilityState === "visible") {
      triggerHaptic(latestSettledRound?.phase === "won" ? "won" : latestSettledRound?.phase === "lost" ? "lost" : "void");
    }
    battleResultFeedbackRef.current = key;
  }, [latestSettledRound?.key, latestSettledRound?.phase]);

  return (
    <section className="active-battle" aria-label={detail.status === "active" ? "Active battle" : "Joined battle"}>
      <div className="battle-title-row">
        <div>
          <span className="eyebrow">{detail.status === "active" ? "ACTIVE" : "JOINED"} · {detail.durationMinutes} MIN</span>
          <h2>{formatPoolSol(detail.prizePoolSol, detail.network)} {getPoolSolLabel(detail.network)} pool</h2>
        </div>
        <button className="icon-action" onClick={() => onShare(detail)} title="Share battle" aria-label="Share battle" type="button"><Share2 size={18} /></button>
      </div>

      <div className="battle-progress">
        <div><span>Round</span><strong>{displayRound?.sequence ?? 1} / {detail.roundCount}</strong></div>
        <div><span>{beforeStart ? "Starts in" : liveRound ? "Round ends" : "Next round"}</span><strong>{formatBattleClock(countdownSeconds)}</strong></div>
        <div><span>Your score</span><strong className="battle-score-value" key={`score-${detail.myEntry?.score ?? 0}`}>{detail.myEntry?.score ?? 0}</strong></div>
        <div><span>Players</span><strong>{detail.participantCount}</strong></div>
      </div>
      <div className="battle-progress-track" aria-label={`${Math.round(progress)}% of battle elapsed`}><span style={{ width: `${progress}%` }} /></div>

      {connection !== "online" ? <ConnectionNotice connection={connection} onReconnect={onReconnect} /> : null}
      {openPickRound ? (
        <BattlePickZone detail={detail} now={now} onPick={onPick} submitting={submitting} timeline={timeline} />
      ) : price !== undefined && priceSeries.length > 0 ? (
        round ? (
          <LiveMarketPanel
            direction={liveRound?.myDirection ?? undefined}
            lineChart={Boolean(liveRound?.myDirection)}
            now={now}
            points={priceSeries}
            price={price}
            round={round}
          >
            {liveRound?.myDirection && livePickStatus ? (
              <div className={`live-trade-banner ${livePickStatus}`}>
                {liveRound.myDirection === "up" ? <ArrowUp size={19} /> : <ArrowDown size={19} />}
                <div>
                  <span>YOUR {liveRound.myDirection.toUpperCase()} PICK</span>
                  <strong>{livePickStatus === "neutral" ? "LIVE" : livePickStatus.toUpperCase()}</strong>
                </div>
              </div>
            ) : null}
          </LiveMarketPanel>
        ) : <BattleMarketMonitor now={now} points={priceSeries} price={price} />
      ) : (
        <BattleMarketWaiting message={roundMessage} price={price} />
      )}


      <div className="demo-balance-bar battle-balance">
        <div><span>{getAvailableSolLabel(detail.network).toUpperCase()}</span><strong>{formatPrimarySol(account?.availableBalance)}</strong><small>{formatPrimarySol(account?.lockedBalance)} locked</small></div>
        <span className="demo-pnl">{beforeStart ? "Market preview" : `Market round ${formatBattleClock(secondsRemaining)}`}</span>
      </div>

      {detail.canLeave ? <button className="load-more battle-leave" disabled={submitting} onClick={() => onLeave(detail.id)} type="button"><LogOut size={16} /> Leave and refund</button> : null}
      {actionMessage ? <div className="battle-message"><ShieldAlert size={15} /> {actionMessage}</div> : null}
      <BattleTimeline detail={detail} now={now} timeline={timeline} />
      <BattleStandings detail={detail} />
    </section>
  );
}

function BattleMarketMonitor({ now, points, price }: {
  now: number;
  points: PricePoint[];
  price: number;
}) {
  const previousPriceRef = useRef(price);
  const previousPrice = previousPriceRef.current;
  const tickDirection = price > previousPrice ? "up" : price < previousPrice ? "down" : "flat";

  useEffect(() => {
    previousPriceRef.current = price;
  }, [price]);

  return (
    <section className="market-panel">
      <div className="price-header">
        <div>
          <div className="eyebrow live-label"><Activity size={13} /> LIVE PRICE</div>
          <div className="current-price"><span className={`price-value tick-${tickDirection}`} key={`monitor:${price}`}>${price.toFixed(2)}</span></div>
          <div className={`price-tick ${tickDirection}`}>{tickDirection === "flat" ? "Awaiting next tick" : `Tick ${formatPriceDelta(price - previousPrice)}`}</div>
        </div>
      </div>
      <PriceChart lineChart={false} now={now} points={points} price={price} />
    </section>
  );
}

function BattleMarketWaiting({ message, price }: { message: string; price?: number }) {
  return (
    <section className="market-panel waiting-market">
      <div className="price-header">
        <div><div className="eyebrow live-label"><Activity size={13} /> LIVE PRICE</div><div className="current-price">{price === undefined ? "$--" : `$${price.toFixed(2)}`}</div></div>
      </div>
      <div className="round-wait-message"><RefreshCw className="spin" size={20} /><strong>{message}</strong></div>
    </section>
  );
}

function BattlePickZone({ detail, now, onPick, submitting, timeline }: {
  detail: CompetitionPoolDetail;
  now: number;
  onPick(direction: Direction, roundId: string): void;
  submitting: boolean;
  timeline: BattleRoundDisplay[];
}) {
  const pickRound = detail.rounds.find((item) => (
    item.status === "scheduled" && now < Date.parse(item.pickClosesAt)
  ));
  const target = pickRound
    ? timeline.find((item) => item.sequence === pickRound.sequence)
    : timeline.find((item) => now < item.pickClosesAt);
  const selectedDirection = pickRound?.myDirection ?? target?.myDirection;
  const pickOpen = Boolean(
    pickRound?.acceptingPick && now >= Date.parse(pickRound.pickOpensAt) && now < Date.parse(pickRound.pickClosesAt),
  );
  const opensIn = target ? Math.max(0, Math.ceil((target.pickOpensAt - now) / 1000)) : 0;
  const closesIn = target ? Math.max(0, Math.ceil((target.pickClosesAt - now) / 1000)) : 0;
  const firstPick = target?.sequence === 1;
  const revealedRound = timeline.find((item) => item.phase === "live" && item.upPicks !== null)
    ?? [...timeline].reverse().find((item) => item.upPicks !== null);
  const upPicks = revealedRound?.upPicks ?? 0;
  const downPicks = revealedRound?.downPicks ?? 0;
  const totalPicks = upPicks + downPicks;
  const upPercent = totalPicks > 0 ? Math.round((upPicks / totalPicks) * 100) : 0;

  return (
    <div className={`battle-pick-zone ${pickOpen ? "pick-open" : selectedDirection ? "pick-locked" : ""}`}>
      <div className="battle-pick-heading">
        <div><span className="eyebrow">{firstPick ? "FIRST PICK" : "NEXT PICK"}</span><strong>{target ? `${formatTime(target.startsAt)} · Round ${target.sequence}` : "Waiting for round"}</strong></div>
        <span>{selectedDirection ? "Locked" : pickOpen ? `Closes in ${closesIn}s` : target ? `Opens in ${formatCompactDuration(opensIn)}` : "Synchronizing"}</span>
      </div>
      <div className="prediction-buttons">
        <button className={`prediction-button up ${selectedDirection === "up" ? "selected" : ""}`} disabled={!pickOpen || Boolean(selectedDirection) || submitting} onClick={() => pickRound && onPick("up", pickRound.roundId)} type="button"><ArrowUp size={24} strokeWidth={3} /><span><strong>PRICE UP</strong><small>Scores 1 when the round closes higher</small></span></button>
        <button className={`prediction-button down ${selectedDirection === "down" ? "selected" : ""}`} disabled={!pickOpen || Boolean(selectedDirection) || submitting} onClick={() => pickRound && onPick("down", pickRound.roundId)} type="button"><ArrowDown size={24} strokeWidth={3} /><span><strong>PRICE DOWN</strong><small>Scores 1 when the round closes lower</small></span></button>
      </div>
      <div className={`prediction-status ${selectedDirection ? "locked" : ""}`}>
        {submitting ? (
          <><RefreshCw className="spin" size={15} /> Locking pick...</>
        ) : selectedDirection ? (
          <><Target size={15} /> {selectedDirection.toUpperCase()} locked · hidden from rivals</>
        ) : detail.status === "open" && detail.participantCount < 2 ? (
          <><Users size={15} /> Waiting for at least two players · entry and fee refund if cancelled</>
        ) : pickOpen ? (
          <><Sparkles size={15} /> Pick closes in {closesIn}s.</>
        ) : target ? (
          <><Clock3 size={15} /> {firstPick ? "First" : "Next"} pick opens in {formatCompactDuration(opensIn)}.</>
        ) : (
          <><RefreshCw className="spin" size={15} /> Preparing the next round...</>
        )}
      </div>
      {revealedRound && totalPicks > 0 ? (
        <div className="battle-market-split" aria-label={`Round ${revealedRound.sequence}: ${upPercent}% up and ${100 - upPercent}% down`}>
          <div className="battle-market-split-heading">
            <span>ROUND {revealedRound.sequence} MARKET SPLIT</span>
            <strong>{totalPicks} picks revealed</strong>
          </div>
          <div className="battle-market-split-values"><span>{upPercent}% UP · {upPicks}</span><span>{100 - upPercent}% DOWN · {downPicks}</span></div>
          <div className="battle-market-split-track"><span style={{ width: `${upPercent}%` }} /></div>
        </div>
      ) : null}
    </div>
  );
}

function getSolTypeLabel(gameMode: GameMode): string {
  return gameMode === "live" ? "SOL" : "Demo SOL";
}

function BattleOverview({ account, actionMessage, detail, gameMode, now, onClose, onJoin, onLeave, onShare, submitting }: {
  account?: DemoAccountView;
  actionMessage?: string;
  detail: CompetitionPoolDetail;
  gameMode: GameMode;
  now: number;
  onClose(): void;
  onJoin(id: string): void;
  onLeave(id: string): void;
  onShare(competition: CompetitionPoolSummary): void;
  submitting: boolean;
}) {
  const [shouldClose, setShouldClose] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleRefundDismissed = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      setShouldClose(true);
      onClose();
    }, 320);
  }, [onClose]);

  if (shouldClose) return null;

  return (
    <section className={`battle-detail ${isExiting ? "is-exiting" : ""}`}>
      <div className="battle-title-row">
        <div><span className="eyebrow">{detail.status.toUpperCase()} · {detail.durationMinutes} MIN</span><h2>{formatPoolSol(detail.prizePoolSol, detail.network)} {getPoolSolLabel(detail.network)} pool</h2></div>
        <button className="icon-action" onClick={() => onShare(detail)} title="Share battle" aria-label="Share battle" type="button"><Share2 size={18} /></button>
      </div>
      <div className="battle-progress battle-summary"><div><span>Players</span><strong>{detail.participantCount}</strong></div><div><span>Entry</span><strong>{formatPoolSol(detail.entryStakeSol, detail.network)}</strong></div><div><span>Fee</span><strong>{formatPoolSol(detail.feeSol, detail.network)}</strong></div></div>
      {detail.status === "open" && detail.joined && detail.participantCount < 2 ? (
        <div className="battle-message" role="status"><Users size={15} /> One more player is required. If nobody joins before the one-minute lock, this pool will cancel and refund you.</div>
      ) : null}
      {detail.status === "cancelled" && detail.cancelReason === "insufficient_players" ? (
        <div className="battle-message" role="status"><ShieldAlert size={15} /> Not enough players joined before the one-minute lock.{detail.myEntry?.status === "refunded" ? " Your entry and fee were refunded." : " No battle was started."}</div>
      ) : null}
      <BattleSettlement account={account} detail={detail} onRefundDismissed={handleRefundDismissed} />
      {detail.canJoin ? <button className="battle-primary" disabled={submitting} onClick={() => window.confirm(`Join for ${formatPoolSol(detail.totalCostSol, detail.network)} ${getPoolSolLabel(detail.network)} total?`) && onJoin(detail.id)} type="button"><LogIn size={17} /> Join pool</button> : null}
      {detail.canLeave ? <button className="load-more" disabled={submitting} onClick={() => onLeave(detail.id)} type="button"><LogOut size={16} /> Leave and refund</button> : null}
      {actionMessage ? <div className="battle-message"><ShieldAlert size={15} /> {actionMessage}</div> : null}
      {detail.status === "completed" ? <><BattleStandings detail={detail} terminal /><BattleTimeline detail={detail} now={now} /></> : null}
    </section>
  );
}

function BattleSettlement({ account, detail, onRefundDismissed }: { account?: DemoAccountView; detail: CompetitionPoolDetail; onRefundDismissed?: () => void }) {
  const entry = detail.myEntry;
  const [phase, setPhase] = useState<"visible" | "exiting" | "hidden">("hidden");

  useEffect(() => {
    if (entry?.status !== "refunded" || !entry?.id) return;

    // Check if we've already shown and dismissed this entry's refund notification
    const dismissedKey = `refund-dismissed-${entry.id}`;
    if (typeof window !== "undefined" && sessionStorage.getItem(dismissedKey)) {
      return;
    }

    // Show notification
    setPhase("visible");

    const dismissTimer = window.setTimeout(() => setPhase("exiting"), 5_000);
    const removeTimer = window.setTimeout(() => {
      setPhase("hidden");
      // Mark as dismissed AFTER notification is hidden
      sessionStorage.setItem(dismissedKey, "true");
      // Close the battle detail view
      onRefundDismissed?.();
    }, 5_320);

    return () => {
      window.clearTimeout(dismissTimer);
      window.clearTimeout(removeTimer);
    };
  }, [entry?.id, entry?.status, onRefundDismissed]);

  if (!entry || (entry.status !== "settled" && entry.status !== "refunded")) return null;
  if (entry.status === "refunded") {
    if (phase === "hidden") return null;
    return (
      <div className={`battle-settlement refunded ${phase === "exiting" ? "is-exiting" : ""}`} role="status">
        <ShieldAlert size={21} />
        <div><span>ENTRY REFUNDED</span><strong>{formatPoolSol(detail.totalCostSol, detail.network)} {getPoolSolLabel(detail.network)}</strong><small>Stake and fee returned · {formatDemoSol(account?.availableBalance)} available</small></div>
      </div>
    );
  }

  const wonPayout = Number(entry.payoutSol) > 0;
  return (
    <div className={`battle-settlement ${wonPayout ? "won" : "lost"}`} role="status">
      {wonPayout ? <Trophy size={22} /> : <Medal size={22} />}
      <div>
        <span>{wonPayout ? "POOL PAYOUT" : "POOL COMPLETE"}</span>
        <strong>{wonPayout ? `${formatPoolSol(entry.payoutSol, detail.network)} ${getPoolSolLabel(detail.network)}` : "No payout"}</strong>
        <small>{formatRank(entry.rank)} · {entry.score} pts · {formatDemoSol(account?.availableBalance)} available</small>
      </div>
    </div>
  );
}

function BattleTimeline({ detail, now, timeline = buildBattleTimeline(detail, now) }: {
  detail: CompetitionPoolDetail;
  now: number;
  timeline?: BattleRoundDisplay[];
}) {
  return (
    <section className="battle-subsection">
      <div className="section-heading"><h2>Round timeline</h2><span>{detail.completedRounds} / {detail.roundCount} settled</span></div>
      <div className="battle-round-timeline" role="list">
        {timeline.map((item) => <BattleRoundTile item={item} key={item.key} now={now} />)}
      </div>
    </section>
  );
}

function BattleRoundTile({ item, now }: { item: BattleRoundDisplay; now: number }) {
  const direction = item.myDirection?.toUpperCase();
  const result = item.result?.toUpperCase();
  const badge = item.phase === "pick-open" ? direction ? "LOCKED" : "PICK NOW"
    : item.phase === "upcoming" ? direction ? "LOCKED" : "WAITING"
      : item.phase.toUpperCase();
  const primary = item.phase === "live" ? formatBattleClock(Math.max(0, Math.ceil((item.endsAt - now) / 1000)))
    : item.phase === "settling" ? "VERIFYING"
      : item.phase === "missed" ? "NO PICK"
        : direction || result || formatTime(item.startsAt);
  const counts = item.upPicks !== null ? ` · ${item.upPicks}↑ ${item.downPicks}↓` : "";
  const detail = item.phase === "live" ? "Round in progress"
    : item.phase === "pick-open" ? `Closes in ${Math.max(0, Math.ceil((item.pickClosesAt - now) / 1000))}s`
      : item.phase === "upcoming" ? `Starts ${formatTime(item.startsAt)}`
        : item.phase === "settling" ? "Result pending"
          : `${result ? `Market ${result}` : "No market result"}${counts}`;

  return (
    <div className={`battle-round ${item.phase}`} role="listitem">
      <div><span>R{item.sequence}</span><b>{badge}</b></div>
      <strong>{primary}</strong>
      <small>{detail}</small>
    </div>
  );
}

function PoolCard({ competition, gameMode, now, onJoin, onLeave, onSelect, onShare, selected, submitting }: {
  competition: CompetitionPoolSummary;
  gameMode: GameMode;
  now: number;
  onJoin(id: string): void;
  onLeave(id: string): void;
  onSelect(id: string): void;
  onShare(competition: CompetitionPoolSummary): void;
  selected: boolean;
  submitting: boolean;
}) {
  const startsIn = Math.max(0, Math.ceil((Date.parse(competition.startsAt) - now) / 1000));
  return (
    <article className={`pool-card ${selected ? "selected" : ""}`} onClick={() => onSelect(competition.id)}>
      <div className="pool-card-top"><span>{competition.durationMinutes} MIN</span><button aria-label="Share pool" title="Share pool" onClick={(event) => { event.stopPropagation(); onShare(competition); }} type="button"><Share2 size={15} /></button></div>
      <strong>{formatPoolSol(competition.prizePoolSol, competition.network)} {getPoolSolLabel(competition.network)}</strong>
      <small><Users size={13} /> {competition.participantCount} players · starts in {formatCompactDuration(startsIn)}</small>
      <div className="pool-cost"><span>Entry {formatPoolSol(competition.entryStakeSol, competition.network)}</span><span>Fee {formatPoolSol(competition.feeSol, competition.network)}</span></div>
      {competition.canJoin ? <button className="pool-join" disabled={submitting} onClick={(event) => { event.stopPropagation(); if (window.confirm(`Join for ${formatPoolSol(competition.totalCostSol, competition.network)} ${getPoolSolLabel(competition.network)} total?`)) onJoin(competition.id); }} type="button"><LogIn size={15} /> Join</button> : competition.canLeave ? <button className="pool-leave" disabled={submitting} onClick={(event) => { event.stopPropagation(); onLeave(competition.id); }} type="button"><LogOut size={15} /> Leave</button> : <span className="pool-joined">{competition.joined ? "JOINED" : "ENTRY CLOSED"}</span>}
    </article>
  );
}

function BattleStandings({ detail, terminal = false }: { detail: CompetitionPoolDetail; terminal?: boolean }) {
  const currentOutside = detail.currentPlayer && !detail.standings.some((entry) => entry.userId === detail.currentPlayer?.userId);
  const entries = currentOutside && detail.currentPlayer
    ? [...detail.standings, detail.currentPlayer]
    : detail.standings;
  return (
    <section className="battle-subsection">
      <div className="section-heading"><h2>Standings</h2><span>{terminal ? "Payouts settled" : "Top 25 + you"}</span></div>
      <div className="list-panel battle-standings">
        {entries.map((entry) => (
          <div className={entry.userId === detail.currentPlayer?.userId ? "current-player" : ""} key={entry.userId}>
            <b>#{entry.rank}</b><span>{entry.displayName}</span>
            <div className="standing-result"><strong>{entry.score} pts</strong>{terminal && Number(entry.payoutSol) > 0 ? <small>{formatDemoSol(entry.payoutSol)} SOL</small> : null}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function HistoryView({
  account,
  cursor,
  embedded = false,
  liveMode,
  mode,
  onLoadMore,
  onRetry,
  records,
  status,
}: {
  account?: DemoAccountView;
  cursor: string | null;
  embedded?: boolean;
  liveMode?: GameModesResponse["live"];
  mode: GameMode;
  onLoadMore(): void;
  onRetry(): void;
  records: UnifiedHistoryItem[];
  status: DataStatus;
}) {
  const completed = mode === "live"
    ? liveMode?.stats.completedTrades ?? 0
    : account?.completedTrades ?? 0;
  const wins = mode === "live"
    ? liveMode?.stats.wins ?? 0
    : account?.wins ?? 0;
  const winRate = mode === "live" ? liveMode?.stats.winRate ?? 0 : account?.winRate ?? 0;
  return (
    <div className={embedded ? "stats-history" : "content-view"}>
      {!embedded ? <ViewHeading eyebrow="YOUR CALLS" title="Prediction history" subtitle="Every locked call and its verified round result." /> : null}
      <div className="summary-strip">
        <div><span>{mode === "live" ? "Live trades" : "Demo trades"}</span><strong>{completed}</strong></div>
        <div><span>Wins</span><strong>{wins}</strong></div>
        <div><span>Win rate</span><strong>{winRate}%</strong></div>
      </div>
      {status === "error" && records.length === 0 ? (
        <DataState action="Retry" message="Prediction history is temporarily unavailable." onAction={onRetry} />
      ) : records.length === 0 && status === "ready" ? (
        <DataState message="Your first prediction will appear here after you lock a call." />
      ) : records.length === 0 ? (
        <DataState loading message="Loading prediction history..." />
      ) : (
        <>
          <div className="list-panel">
            {records.map((record) => (
              <article className="history-row" key={record.id}>
                <div className={`direction-icon ${record.direction}`}>
                  {record.direction === "up" ? <ArrowUp size={18} /> : <ArrowDown size={18} />}
                </div>
                <div className="history-main">
                  <strong>SOL {record.direction}</strong>
                  <span>{record.mode === "demo"
                    ? `${formatDemoSol(record.stakeSol)} Demo SOL stake · ${formatDemoSol(record.feeSol)} fee · `
                    : record.mode === "live"
                      ? `${formatSolAmount(record.stakeSol)} SOL stake · ${formatSolAmount(record.feeSol)} fee · `
                      : "Free · "}{formatTime(Date.parse(record.roundStartsAt))} UTC · {formatPriceMove(record)}</span>
                </div>
                <div className={`outcome ${record.outcome}`}>
                  <strong>{record.outcome === "pending" ? "in progress" : record.outcome}</strong>
                  <span>{record.settledAt ? formatTime(Date.parse(record.settledAt)) + " UTC" : "Awaiting result"}</span>
                </div>
              </article>
            ))}
          </div>
          {cursor ? (
            <button className="load-more" disabled={status === "loading"} onClick={onLoadMore} type="button">
              {status === "loading" ? <RefreshCw className="spin" size={16} /> : <History size={16} />}
              {status === "loading" ? "Loading..." : "Load more"}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

function StatsView({ account, cursor, historyStatus, liveMode, mode, onLoadMore, onRetryAccount, onRetryHistory, records, status }: {
  account?: DemoAccountView;
  cursor: string | null;
  historyStatus: DataStatus;
  liveMode?: GameModesResponse["live"];
  mode: GameMode;
  onLoadMore(): void;
  onRetryAccount(): void;
  onRetryHistory(): void;
  records: UnifiedHistoryItem[];
  status: DataStatus;
}) {
  const [section, setSection] = useState<"overview" | "history">("overview");
  return (
    <div className="content-view">
      <ViewHeading
        eyebrow={mode === "live" ? `${liveMode?.network === "devnet" ? "DEVNET" : "LIVE"} TRADING` : "PAPER TRADING"}
        title={mode === "live" ? "Live stats" : "Demo stats"}
        subtitle={mode === "live" ? "SOL performance and settled round results." : "Track Demo SOL performance and verified calls."}
      />
      <div className="metric-tabs" role="tablist" aria-label="Stats view">
        <button aria-selected={section === "overview"} className={section === "overview" ? "active" : ""} onClick={() => setSection("overview")} role="tab" type="button">Overview</button>
        <button aria-selected={section === "history"} className={section === "history" ? "active" : ""} onClick={() => setSection("history")} role="tab" type="button">History</button>
      </div>
      {section === "history" ? <HistoryView account={account} cursor={cursor} embedded liveMode={liveMode} mode={mode} onLoadMore={onLoadMore} onRetry={onRetryHistory} records={records} status={historyStatus} /> : (
        <>
          {mode === "live" ? (
            <div className="stat-grid demo-stat-grid">
              <div className="stat-tile accent"><Zap size={20} /><span>Available SOL</span><strong>{formatPrimarySol(liveMode?.balance.availableSol)}</strong></div>
              <div className="stat-tile"><Activity size={20} /><span>Net PnL</span><strong>{formatSignedPrimarySol(liveMode?.stats.netPnlSol)}</strong></div>
              <div className="stat-tile"><Flame size={20} /><span>Current streak</span><strong>{liveMode?.stats.currentStreak ?? 0}</strong></div>
              <div className="stat-tile"><Zap size={20} /><span>Best streak</span><strong>{liveMode?.stats.bestStreak ?? 0}</strong></div>
              <div className="stat-tile"><Gamepad2 size={20} /><span>Completed trades</span><strong>{liveMode?.stats.completedTrades ?? 0}</strong></div>
              <div className="stat-tile"><Trophy size={20} /><span>Wins</span><strong>{liveMode?.stats.wins ?? 0}</strong></div>
              <div className="stat-tile"><Target size={20} /><span>Win rate</span><strong>{liveMode?.stats.winRate ?? 0}%</strong></div>
              <div className="stat-tile"><ShieldAlert size={20} /><span>Wallet total</span><strong>{formatPrimarySol(liveMode?.balance.totalSol)}</strong></div>
            </div>
          ) : !account && status === "error" ? <DataState action="Retry" message="Demo account is temporarily unavailable." onAction={onRetryAccount} /> : !account ? <DataState loading message="Loading Demo SOL account..." /> : (
            <div className="stat-grid demo-stat-grid">
              <div className="stat-tile accent"><Zap size={20} /><span>Available Demo SOL</span><strong>{formatPrimarySol(account.availableBalance)}</strong></div>
              <div className="stat-tile"><Activity size={20} /><span>Net PnL</span><strong>{formatPrimarySol(account.netPnl)}</strong></div>
              <div className="stat-tile"><Flame size={20} /><span>Current streak</span><strong>{account.currentStreak}</strong></div>
              <div className="stat-tile"><Zap size={20} /><span>Best streak</span><strong>{account.bestStreak}</strong></div>
              <div className="stat-tile"><Gamepad2 size={20} /><span>Completed trades</span><strong>{account.completedTrades}</strong></div>
              <div className="stat-tile"><Trophy size={20} /><span>Wins</span><strong>{account.wins}</strong></div>
              <div className="stat-tile"><Target size={20} /><span>Win rate</span><strong>{account.winRate}%</strong></div>
              <div className="stat-tile"><ShieldAlert size={20} /><span>Equity</span><strong>{formatPrimarySol(account.equity)}</strong></div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
function ProfileView({ name, onRetry, user, demoAccount, demoStatus, wallet, walletDeposits, walletWithdrawals, walletStatus, onCancelWithdrawal, onSubmitWithdrawal, onReset, onReplayTutorial, referral, referralCode, referralMessage, referralSubmitting, onReferralCodeChange, onActivateReferral, onRefreshReferral, gameMode, gameModes, onGameModeChange }: {
  name: string;
  onRetry(): void;
  user?: AuthenticatedUser;
  demoAccount?: DemoAccountView;
  demoStatus: DataStatus;
  wallet?: SolanaWalletResponse;
  gameMode: GameMode;
  gameModes?: GameModesResponse;
  walletDeposits?: SolanaDepositHistoryResponse;
  walletWithdrawals?: SolanaWithdrawalHistoryResponse;
  walletStatus: DataStatus;
  onCancelWithdrawal(withdrawalId: string): Promise<string | undefined>;
  onSubmitWithdrawal(destinationAddress: string, amountSol: string, idempotencyKey: string): Promise<string | undefined>;
  onReset(): void;
  onReplayTutorial(): void;
  referral?: ReferralMeResponse;
  referralCode: string;
  referralMessage?: string;
  referralSubmitting: boolean;
  onReferralCodeChange(value: string): void;
  onActivateReferral(): void;
  onRefreshReferral(): void;
  onGameModeChange(mode: GameMode): void;
}) {
  return (
    <div className="content-view">
      <section className="profile-hero">
        <div className="profile-avatar">{initials(name)}</div>
        <div><span>PLAYER PROFILE</span><h1>{name}</h1><p>{user?.username ? `@${user.username}` : "Telegram player"} · {gameMode === "live" ? (gameModes?.live.network === "devnet" ? "Devnet mode" : "Live mode") : "Demo mode"}</p></div>
      </section>
      <a className="profile-contact" href={communityUrl} rel="noreferrer" target="_blank"><Users size={17} /><span><strong>PriceOn Community</strong><small>Connect with us on Telegram</small></span><ArrowUpRight size={16} /></a>
      {gameMode === "demo" ? (
        <section className="profile-section demo-balance-section">
          <div className="section-heading"><h2>Demo SOL balance</h2></div>
          {demoStatus === "error" ? (
            <DataState action="Retry" message="Demo account is temporarily unavailable." onAction={onRetry} />
          ) : !demoAccount ? (
            <DataState loading message="Loading Demo SOL account..." />
          ) : (
            <>
              <div className="demo-balance-display">
                <div><span>Available</span><strong>{formatPrimarySol(demoAccount.availableBalance)} Demo SOL</strong></div>
                <div><span>Locked</span><strong>{formatPrimarySol(demoAccount.lockedBalance)} Demo SOL</strong></div>
              </div>
              <p className="demo-balance-copy">Demo SOL is virtual currency for risk-free trading practice. It cannot be deposited, withdrawn, or converted to real SOL.</p>
            </>
          )}
        </section>
      ) : (
        <>
          <WalletSection
            deposits={walletDeposits}
            onCancelWithdrawal={onCancelWithdrawal}
            onRetry={onRetry}
            onSubmitWithdrawal={onSubmitWithdrawal}
            response={wallet}
            status={walletStatus}
            withdrawals={walletWithdrawals}
          />
          <ReferralSection code={referralCode} message={referralMessage} onActivate={onActivateReferral} onChange={onReferralCodeChange} onRefresh={onRefreshReferral} referral={referral} submitting={referralSubmitting} />
        </>
      )}
      {gameMode === "demo" ? (
        <ReferralSection code={referralCode} message={referralMessage} onActivate={onActivateReferral} onChange={onReferralCodeChange} onRefresh={onRefreshReferral} referral={referral} submitting={referralSubmitting} />
      ) : null}
      {demoAccount ? <section className="profile-section demo-actions"><div className="section-heading"><h2>Demo account</h2><span>{demoAccount.resetCount} resets</span></div><p>{demoAccount.canReset ? "Your demo balance is ready for another grant." : "Use the demo balance to practice risk-free trades."}</p><button className="load-more" disabled={!demoAccount.canReset} onClick={onReset} type="button"><RefreshCw size={16} /> Reset to 10.00 SOL</button><button className="load-more" onClick={onReplayTutorial} type="button"><Sparkles size={16} /> Replay tutorial</button></section> : null}
    </div>
  );
}

function ReferralSection({ code, message, onActivate, onChange, onRefresh, referral, submitting }: {
  code: string; message?: string; onActivate(): void; onChange(value: string): void; onRefresh(): void;
  referral?: ReferralMeResponse; submitting: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const share = () => {
    if (!referral?.code) return;
    const directUrl = botUsername ? `https://t.me/${botUsername}?startapp=ref_${referral.code}` : `${window.location.origin}?ref=ref_${referral.code}`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(directUrl)}&text=${encodeURIComponent("Unlock PriceOn Live with my referral")}`;
    if (window.Telegram?.WebApp?.openTelegramLink) window.Telegram.WebApp.openTelegramLink(url);
    else window.open(url, "_blank", "noopener,noreferrer");
  };
  const copy = async () => {
    if (!referral?.code) return;
    await navigator.clipboard.writeText(referral.code).catch(() => undefined);
    setCopied(true); window.setTimeout(() => setCopied(false), 1_500);
  };
  const monthlyNgr = referral?.commission?.model === "monthly_ngr";
  return <section className="profile-section referral-section">
    <div className="section-heading"><h2>Live referral</h2><span>{referral?.code ? `${referral.activations.length} invited` : referral?.enabled === false ? "Paused" : "Live access"}</span></div>
    {referral?.code ? <><div className="referral-code-row"><code>{referral.code}</code><button className="icon-button" title="Copy referral code" aria-label="Copy referral code" onClick={() => void copy()} type="button"><Copy size={16} /></button><button className="icon-button" title="Share referral link" aria-label="Share referral link" onClick={share} type="button"><Share2 size={16} /></button></div><small className="wallet-copy-state">{copied ? "Referral code copied" : monthlyNgr ? "You earn 20% of each invited user's positive monthly net gaming revenue after hedge adjustments and carryover." : "Legacy referral rewards remain active until the monthly NGR cutover."}</small>{referral.commission ? <div className="referral-balances"><div><span>This month estimate</span><strong>{referral.commission.estimatedSol} SOL</strong></div><div><span>Closed / reviewing</span><strong>{referral.commission.reviewingSol} SOL</strong></div><div><span>Total paid</span><strong>{referral.commission.paidSol} SOL</strong></div><small>{monthlyNgr ? `UTC month · 7-day hold${referral.commission.nextReviewAt ? ` · next review ${new Date(referral.commission.nextReviewAt).toLocaleDateString()}` : ""}` : "Paid total includes legacy referral rebates."}</small></div> : null}</> : null}
    {!referral?.code ? <div className="referral-activate"><input aria-label="Referral code" autoCapitalize="characters" placeholder="Referral code" value={code} onChange={(event) => onChange(event.target.value.toUpperCase().replace(/[^A-Z2-9]/g, ""))} /><button className="load-more" disabled={submitting || code.length < 8} onClick={onActivate} type="button"><Zap size={16} />{submitting ? "Activating..." : "Unlock Live"}</button></div> : null}
    {message ? <small className="wallet-copy-state">{message}</small> : !referral ? <button className="load-more" onClick={onRefresh} type="button"><RefreshCw size={16} /> Retry</button> : null}
  </section>;
}

function WalletSection({ deposits, onCancelWithdrawal, onRetry, onSubmitWithdrawal, response, status, withdrawals }: {
  deposits?: SolanaDepositHistoryResponse;
  onCancelWithdrawal(withdrawalId: string): Promise<string | undefined>;
  onRetry(): void;
  onSubmitWithdrawal(destinationAddress: string, amountSol: string, idempotencyKey: string): Promise<string | undefined>;
  response?: SolanaWalletResponse;
  status: DataStatus;
  withdrawals?: SolanaWithdrawalHistoryResponse;
}) {
  const [copied, setCopied] = useState(false);
  const [withdrawalOpen, setWithdrawalOpen] = useState(false);
  const [cancellingWithdrawalId, setCancellingWithdrawalId] = useState<string>();
  const [withdrawalMessage, setWithdrawalMessage] = useState<string>();
  if (status === "error") {
    return <section className="profile-section"><DataState action="Retry" message="Your SOL wallet is temporarily unavailable." onAction={onRetry} /></section>;
  }
  if (!response) {
    return <section className="profile-section"><DataState loading message="Preparing your SOL deposit wallet..." /></section>;
  }
  const { wallet } = response;
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(wallet.address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1_500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <section className="profile-section wallet-section">
      <div className="section-heading"><h2>SOL wallet</h2><span>{wallet.network}</span></div>
      <div className="wallet-balances">
        <div><span>Available</span><strong>{formatPrimarySol(wallet.balance.availableSol)} SOL</strong></div>
        <div><span>Pending</span><strong>{formatPrimarySol(wallet.balance.pendingSol)} SOL</strong></div>
      </div>
      <div className="wallet-address-row">
        <code title={wallet.address}>{wallet.address}</code>
        <button aria-label="Copy deposit address" className="icon-button" onClick={() => void copyAddress()} title="Copy deposit address" type="button"><Copy size={16} /></button>
      </div>
      <small className="wallet-copy-state">{copied ? "Address copied" : `Send ${wallet.network} SOL to this deposit address.`}</small>
      <button className="wallet-withdraw" onClick={() => setWithdrawalOpen(true)} type="button"><Send size={16} /> Withdraw SOL</button>
      {deposits?.items.length ? (
        <div className="wallet-deposits">
          <div className="section-heading"><h2>Recent deposits</h2><span>{deposits.items.length}</span></div>
          {deposits.items.map((deposit) => (
            <div className="wallet-deposit-row" key={deposit.id}>
              <div><strong>+{deposit.amountSol} SOL</strong><span>{formatDepositTime(deposit.detectedAt)}</span></div>
              <b className={deposit.status}>{(deposit.status === "sweep_pending" || deposit.status === "swept") ? "deposited" : deposit.status.replace("_", " ")}</b>
            </div>
          ))}
        </div>
      ) : <span className="profile-empty">No deposits yet.</span>}
      <div className="wallet-withdrawals">
        <div className="section-heading"><h2>Recent withdrawals</h2><span>{withdrawals?.items.length ?? 0}</span></div>
        {withdrawals?.items.length ? withdrawals.items.map((withdrawal) => (
          <div className="wallet-withdrawal-row" key={withdrawal.id}>
            <div><strong>-{withdrawal.amountSol} SOL</strong><span title={withdrawal.destinationAddress}>{withdrawal.destinationAddress.slice(0, 8)}...{withdrawal.destinationAddress.slice(-6)}</span></div>
            <div className="withdrawal-status"><b className={withdrawal.status}>{withdrawal.status.replace("_", " ")}</b>{withdrawal.cancelable ? <button className="withdrawal-cancel" disabled={Boolean(cancellingWithdrawalId)} onClick={async () => { setCancellingWithdrawalId(withdrawal.id); const error = await onCancelWithdrawal(withdrawal.id); setWithdrawalMessage(error || "Withdrawal cancelled."); setCancellingWithdrawalId(undefined); }} type="button">{cancellingWithdrawalId === withdrawal.id ? "Cancelling..." : "Cancel"}</button> : null}</div>
          </div>
        )) : <span className="profile-empty">No withdrawals yet.</span>}
        {withdrawalMessage ? <span className="wallet-copy-state">{withdrawalMessage}</span> : null}
      </div>
      {withdrawalOpen ? (
        <WithdrawalModal
          network={wallet.network}
          onClose={() => setWithdrawalOpen(false)}
          onSubmit={onSubmitWithdrawal}
        />
      ) : null}
    </section>
  );
}

function WithdrawalModal({ network, onClose, onSubmit }: {
  network: SolanaWalletResponse["wallet"]["network"];
  onClose(): void;
  onSubmit(destinationAddress: string, amountSol: string, idempotencyKey: string): Promise<string | undefined>;
}) {
  const [destinationAddress, setDestinationAddress] = useState("");
  const [amountSol, setAmountSol] = useState("");
  const [stage, setStage] = useState<"form" | "review">("form");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string>();
  const [idempotencyKey] = useState(() => (
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `withdrawal-${Date.now()}-${Math.random().toString(36).slice(2)}`
  ));
  const reviewReady = destinationAddress.trim().length >= 32 && amountSol.trim().length > 0;

  const review = () => {
    setMessage(undefined);
    if (!reviewReady) {
      setMessage("Enter a valid Solana address and withdrawal amount.");
      return;
    }
    setStage("review");
  };

  const submit = async () => {
    setSubmitting(true);
    setMessage(undefined);
    const error = await onSubmit(destinationAddress.trim(), amountSol.trim(), idempotencyKey);
    setSubmitting(false);
    if (error) {
      setMessage(error);
      return;
    }
    onClose();
  };

  return (
    <div className="withdrawal-backdrop" role="presentation">
      <div aria-labelledby="withdrawal-title" aria-modal="true" className="withdrawal-modal" role="dialog">
        <button aria-label="Close withdrawal" className="withdrawal-close" onClick={onClose} title="Close" type="button"><X size={18} /></button>
        <div className="withdrawal-icon"><Send size={20} /></div>
        <h1 id="withdrawal-title">Withdraw SOL</h1>
        {stage === "form" ? (
          <>
            <p className="withdrawal-copy">Send your real SOL balance to an external Solana address.</p>
            <label className="withdrawal-field"><span>Destination address</span><input autoComplete="off" onChange={(event) => setDestinationAddress(event.target.value)} placeholder="Paste a Solana address" spellCheck={false} value={destinationAddress} /></label>
            <label className="withdrawal-field"><span>Amount (SOL)</span><input inputMode="decimal" onChange={(event) => setAmountSol(event.target.value)} placeholder="0.01" step="0.000000001" type="number" value={amountSol} /></label>
            <small className="withdrawal-hint">Minimum 0.01 SOL · Maximum: available balance · Network fee is covered by PriceOn.</small>
            {message ? <p className="withdrawal-error" role="alert">{message}</p> : null}
            <button className="withdrawal-primary" disabled={!reviewReady} onClick={review} type="button">Review withdrawal <ArrowUpRight size={16} /></button>
          </>
        ) : (
          <>
            <p className="withdrawal-copy">Check the destination carefully. A blockchain transfer cannot be reversed.</p>
            <div className="withdrawal-summary">
              <div><span>Destination</span><code>{destinationAddress.trim()}</code></div>
              <div><span>You receive</span><strong>{amountSol.trim()} SOL</strong></div>
              <div><span>Network</span><strong>{network}</strong></div>
            </div>
            {message ? <p className="withdrawal-error" role="alert">{message}</p> : null}
            <div className="withdrawal-actions"><button className="withdrawal-secondary" disabled={submitting} onClick={() => { setMessage(undefined); setStage("form"); }} type="button"><ArrowLeft size={16} /> Back</button><button className="withdrawal-primary" disabled={submitting} onClick={() => void submit()} type="button">{submitting ? "Submitting..." : "Confirm withdrawal"} <Send size={16} /></button></div>
          </>
        )}
      </div>
    </div>
  );
}

function ViewHeading({ eyebrow, subtitle, title }: { eyebrow: string; subtitle: string; title: string }) {
  return <header className="view-heading"><span>{eyebrow}</span><h1>{title}</h1><p>{subtitle}</p></header>;
}

function DataState({ action, loading, message, onAction }: {
  action?: string;
  loading?: boolean;
  message: string;
  onAction?: () => void;
}) {
  return (
    <div className="data-state" aria-busy={loading || undefined}>
      {loading ? <RefreshCw className="spin" size={20} /> : <ShieldAlert size={20} />}
      <span>{message}</span>
      {action && onAction ? <button onClick={onAction} type="button">{action}</button> : null}
    </div>
  );
}

function formatRank(rank: number | null): string {
  return rank === null ? "Unranked" : `#${rank}`;
}

function formatPriceMove(record: { startPrice: string | null; closePrice: string | null }): string {
  if (record.startPrice === null) return "Round cancelled";
  if (record.closePrice === null) return `$${Number(record.startPrice).toFixed(2)} → live round`;
  return `$${Number(record.startPrice).toFixed(2)} → $${Number(record.closePrice).toFixed(2)}`;
}

function formatDemoSol(value: string | null | undefined): string {
  return formatDemoDetailSol(value);
}

function formatSolAmount(value: string | null | undefined): string {
  return formatLiveDetailSol(value);
}

function formatPoolSol(value: string | null | undefined, network: "demo" | "devnet" | "mainnet"): string {
  return formatDemoDetailSol(value);
}

function getPoolSolLabel(network: "demo" | "devnet" | "mainnet"): string {
  return network === "demo" ? "Demo SOL" : "SOL";
}

function getAvailableSolLabel(network: "demo" | "devnet" | "mainnet"): string {
  return network === "demo" ? "Available Demo SOL" : "Available SOL";
}

function liveUnavailableMessage(reason: GameModesResponse["live"]["reason"] | undefined): string {
  if (reason === "worker_unavailable") return "Live wallet processing is temporarily unavailable.";
  if (reason === "reserve_unavailable") return "Live payout capacity is temporarily unavailable.";
  if (reason === "not_invited") return "Live mode requires an invitation.";
  return "Live trading is currently disabled.";
}

function createIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function toUnifiedLiveHistoryItem(item: LiveHistoryResponse["items"][number]): UnifiedHistoryItem {
  return {
    id: item.id,
    roundId: item.roundId,
    direction: item.direction,
    entryPrice: null, // Live history doesn't include entry_price
    outcome: item.status,
    mode: "live",
    stakeSol: item.stakeSol,
    feeSol: item.feeSol,
    totalCostSol: item.totalCostSol,
    pnlSol: item.pnlSol,
    symbol: item.symbol,
    roundStartsAt: item.roundStartsAt,
    roundEndsAt: item.roundEndsAt,
    startPrice: item.startPrice,
    closePrice: item.closePrice,
    createdAt: item.createdAt,
    settledAt: item.settledAt,
  };
}

function getCompetitionStartId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const value = window.Telegram?.WebApp?.initDataUnsafe?.start_param
    || new URLSearchParams(window.location.search).get("pool")
    || undefined;
  const match = value?.match(/^pool_([1-9]\d*)$/);
  return match?.[1];
}

function getReferralStartCode(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const value = window.Telegram?.WebApp?.initDataUnsafe?.start_param
    || new URLSearchParams(window.location.search).get("ref")
    || undefined;
  return value?.match(/^ref_([A-Z2-9]{8,32})$/i)?.[1]?.toUpperCase();
}

function formatCompactDuration(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

function formatBattleClock(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function wasBattleAlertHandled(competitionId: string): boolean {
  try {
    return window.sessionStorage.getItem(`priceon:battle-alert:${competitionId}`) === "handled";
  } catch {
    return false;
  }
}

function markBattleAlertHandled(competitionId: string): void {
  try {
    window.sessionStorage.setItem(`priceon:battle-alert:${competitionId}`, "handled");
  } catch {
    // A blocked session store should not prevent the alert action.
  }
}

function BattleStartModal({ competition, onDismiss, onOpen }: {
  competition: CompetitionPoolSummary;
  onDismiss(): void;
  onOpen(): void;
}) {
  return (
    <div className="battle-alert-backdrop" role="dialog" aria-modal="true" aria-labelledby="battle-alert-title">
      <section className="battle-alert-modal">
        <button className="battle-alert-close" aria-label="Dismiss battle alert" title="Dismiss" onClick={onDismiss} type="button"><X size={18} /></button>
        <div className="battle-alert-icon"><Swords size={26} /></div>
        <span className="eyebrow">BATTLE READY</span>
        <h1 id="battle-alert-title">Your battle starts in 15 seconds</h1>
        <p>The room is locked and your first SOL prediction is open now.</p>
        <div className="battle-alert-summary">
          <div><span>Duration</span><strong>{competition.durationMinutes} min</strong></div>
          <div><span>Prize pool</span><strong>{formatDemoSol(competition.prizePoolSol)}</strong></div>
          <div><span>Starts</span><strong>{formatTime(Date.parse(competition.startsAt))}</strong></div>
        </div>
        <button className="battle-alert-open" onClick={onOpen} type="button"><Swords size={17} /> Open battle</button>
      </section>
    </div>
  );
}

function TutorialModal({ onComplete, onSkip }: { onComplete(): void; onSkip(): void }) {
  const [step, setStep] = useState(0);
  const steps = [
    { title: "Your Demo SOL balance", copy: "You start with 10.00 virtual SOL. It cannot be deposited, withdrawn, or converted to real SOL." },
    { title: "Choose your stake", copy: "Use a preset, enter an amount from 0.01 SOL, or select Max. A 1% fee is added to every trade." },
    { title: "Call the next round", copy: "During the final 15 seconds, choose whether SOL will close above or below the next round's start price." },
    { title: "Review the result", copy: "A win returns 2x the stake. Cancelled rounds refund both the stake and fee." },
  ];
  const current = steps[step]!;
  return <div className="tutorial-backdrop" role="dialog" aria-modal="true" aria-labelledby="tutorial-title"><section className="tutorial-modal"><span className="eyebrow">DEMO MODE · {step + 1} / {steps.length}</span><h1 id="tutorial-title">{current.title}</h1><p>{current.copy}</p><div className="tutorial-progress" aria-hidden="true">{steps.map((_, index) => <span className={index <= step ? "active" : ""} key={index} />)}</div><div className="tutorial-actions"><button className="load-more" onClick={onSkip} type="button">Skip for now</button>{step < steps.length - 1 ? <button className="gate-action" onClick={() => setStep((value) => value + 1)} type="button">Next <ArrowUp size={17} /></button> : <button className="gate-action" onClick={onComplete} type="button"><Sparkles size={17} /> Start demo</button>}</div></section></div>;
}

function BottomNavigation({ activeTab, battlesEnabled, onChange }: { activeTab: Tab; battlesEnabled: boolean; onChange(tab: Tab): void }) {
  const items: Array<{ id: Tab; icon: typeof Gamepad2; label: string }> = [
    { id: "play", icon: Gamepad2, label: "Play" },
    ...(battlesEnabled ? [{ id: "battle" as const, icon: Swords, label: "Battle" }] : []),
    { id: "stats", icon: Trophy, label: "Stats" },
    { id: "profile", icon: UserRound, label: "Profile" },
  ];
  return <nav className="bottom-nav" aria-label="Primary navigation" style={{ gridTemplateColumns: `repeat(${items.length}, 1fr)` }}>{items.map((item) => { const Icon = item.icon; return <button aria-current={activeTab === item.id ? "page" : undefined} className={activeTab === item.id ? "active" : ""} key={item.id} onClick={() => onChange(item.id)} type="button"><Icon size={20} /><span>{item.label}</span></button>; })}</nav>;
}

function AuthGate({ message, onRetry, state }: { message: string; onRetry(): void; state: AuthState }) {
  const checking = state === "checking";
  if (checking) {
    return (
      <main className="loading-shell" aria-busy="true" aria-live="polite">
        <div className="loading-emblem" aria-hidden="true">
          <Zap size={42} fill="currentColor" strokeWidth={2.5} />
        </div>
        <strong>Loading</strong>
      </main>
    );
  }

  return (
    <main className="gate-shell">
      <div className="gate-brand"><span className="brand-mark"><Zap size={22} fill="currentColor" /></span><strong>PriceOn</strong></div>
      <section className="gate-content">
        <div className="gate-icon"><ShieldAlert size={30} /></div>
        <span className="eyebrow">TELEGRAM MINI APP</span>
        <h1>{state === "registration-required" ? "Create your player" : "Session unavailable"}</h1>
        <p>{message}</p>
        {state === "registration-required" && botUsername ? <a className="gate-action" href={`https://t.me/${botUsername}?start=priceon`}>Open PriceOn Bot <ArrowUp size={17} /></a> : null}
        {state === "error" ? <button className="gate-action" onClick={onRetry} type="button"><RefreshCw size={17} /> Try again</button> : null}
      </section>
      <p className="gate-footnote">45-second SOL rounds · 15-second decisions · No wallet required</p>
    </main>
  );
}

function formatTime(timestamp: number): string {
  return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" }).format(timestamp) + " UTC";
}

function formatDepositTime(value: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(Date.parse(value));
}

function getPriceStreamUrl(): string {
  if (typeof window !== "undefined" && window.location.hostname === "localhost") {
    return "ws://localhost:4000/prices/stream";
  }
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/ws/prices/stream`;
}

function initials(name: string): string {
  return name.replace("@", "").split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
}
